import React, { useEffect, useState, useMemo, useRef } from "react";
import * as Yup from "yup";
import FormBuilder from "../../../../../../../shared/components/form-builder/form-builder";
import { useApi } from "../../../../../../../shared/hooks/useApi";
import { useParams } from "react-router-dom";
import { TCAppConfig } from "../../../../../../../shared/app-configs/tc-app-configs";
import { Alert, AlertTitle, Autocomplete, Box, Chip, styled, TextField, Typography } from "@mui/material";
import { useTheme } from '@mui/material/styles';
import { dtrackItService } from "../../../../tc-service/dtrackit.service";
import { buyerSellerDetailsService } from "../../../../tc-service/buyer-seller-details.service";
import { FormConfigProps } from "../../../../../../../shared/components/form-builder/form-builder.model";
import { useFormActions } from "../../../../../../../shared/hooks/form-builder.hooks";
import { standardCodeFromValue, hasCertTypeToken } from "../incoming-details-apis";
import {
  buildRmdfArticleMaps,
  ensureExternalIncomingCertificateTypes,
  extractRmdfPercentage,
  filterIncomingCertificateTypes,
  getIncomingTcSourceVersion,
  unwrapRmdfLookupItems,
} from "./incoming-rmdf.utils";
import { buildIncomingSourceLookupKey } from "./incoming-source-state.utils";
import { useFormStore, useTransactionCertificateStore } from "../../../../../../../store/transaction-certificates.store";
import useNotification from "../../../../../../../shared/hooks/useNotification";
import { StyledButton } from "../../../../../../../shared/components/buttons/custom-button";

interface IncomingTcInfoProps {
  formRef?: React.RefObject<any>;
  componentData: any;
  setComponentData: (data: any) => void;
  onValidationChange?: (isValidated: boolean) => void;
}

interface Option {
  value: string;
  label: string;
  tcId?: string;
  atcId?: string;
  mdfId?: string;
  fpfId?: string;
  sourceVersion?: string;
}

type IncomingSourceType = "V3" | "V4" | "RMDF" | "FPF" | "ATU" | "GOTS" | "";
type IncomingV4IssuedFlag = "Y" | "N";

const TC_STANDARDS = [
  { value: "CCS", label: "CCS" },
  { value: "GRS", label: "GRS" },
  { value: "OCS", label: "OCS" },
  { value: "RCS", label: "RCS" },
  { value: "RWS", label: "RWS" },
  { value: "RMS", label: "RMS" },
  { value: "RAS", label: "RAS" },
  { value: "RDS", label: "RDS" },
  { value: "MMS", label: "MMS" },
  { value: "GOTS", label: "GOTS" },
];

type IncomingRawMaterialSource = {
  tcProductCategoryCode?: string;
  tcProductDetailCode?: string;
  rawMaterialCode?: string;
  rawMaterialPercentage?: number;
  standard?: string;
  isCertified?: string;
  tcProductNetShippingWeight?: number;
  tcProductSupplementaryWeight?: number;
  tcProductAdditivesWeight?: number;
};

const DISALLOWED_INCOMING_PLACEHOLDERS = ["na", "n/a", "not available", "not applicable", "null"];
const hasValidIncomingText = (value?: string | null) => {
  const normalized = String(value || "").trim().toLowerCase();
  return normalized !== "" && !DISALLOWED_INCOMING_PLACEHOLDERS.includes(normalized);
};
const normalizeIncomingV4IssuedFlag = (value: any): IncomingV4IssuedFlag =>
  String(value || "").trim().toUpperCase() === "Y" ? "Y" : "N";
const extractLeadingCode = (value: any): string => {
  const text = String(value || "").trim();
  if (!text) return "";
  const directMatch = text.match(/\b(?:PC|PD|RM)\d{4}\b/i);
  if (directMatch) return directMatch[0].toUpperCase();
  return text.split(" - ")[0].trim();
};
const toNumberOrZero = (value: any): number => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : 0;
};

const extractPercentage = (item: any, rawMaterialCode?: string): number =>
  extractRmdfPercentage(item, rawMaterialCode);

const addUniqueRawMaterial = (
  target: IncomingRawMaterialSource[],
  row: IncomingRawMaterialSource
) => {
  const key = [
    row.tcProductCategoryCode || "",
    row.tcProductDetailCode || "",
    row.rawMaterialCode || "",
    Number(row.rawMaterialPercentage || 0),
    row.standard || "",
  ].join("|");
  const exists = target.some((item) => [
    item.tcProductCategoryCode || "",
    item.tcProductDetailCode || "",
    item.rawMaterialCode || "",
    Number(item.rawMaterialPercentage || 0),
    item.standard || "",
  ].join("|") === key);
  if (!exists) target.push(row);
};

const normalizeStandardValues = (value: any): string[] => {
  const values = Array.isArray(value) ? value : value ? [value] : [];
  const flattened = typeof (Array.prototype as any).flat === "function"
    ? (values as any).flat(Infinity)
    : values.reduce((acc: any[], cur: any) => acc.concat(Array.isArray(cur) ? cur : [cur]), []);
  return Array.from(new Set(flattened.map((item: any) => String(item || "").trim()).filter(Boolean)));
};

const unwrapIncomingProducts = (payload: any): any[] => {
  if (!payload) return [];
  let data = payload;
  if (typeof data === "string") {
    try {
      data = JSON.parse(data);
    } catch {
      return [];
    }
  }
  if (data?.response !== undefined) return unwrapIncomingProducts(data.response);
  const rows = Array.isArray(data) ? data : [data];
  return rows.flatMap((row: any) => {
    const value = row?.trackitPortalData ?? row?.fpfLotNumberInformation ?? row?.fpfLotNumberDetails ?? row;
    return Array.isArray(value) ? value : [value];
  }).filter(Boolean);
};

const buildIncomingArticleMaps = (products: any[]) => {
  const standardsByArticle: { [productNo: string]: string[] } = {};
  const rawMaterialsByArticle: { [productNo: string]: IncomingRawMaterialSource[] } = {};
  const maxWeightByArticle: { [productNo: string]: number } = {};
  const sourceByArticle: { [productNo: string]: IncomingRawMaterialSource } = {};
  const standards: string[] = [];

  unwrapIncomingProducts(products).forEach((product: any) => {
    const articleNo = String(
      product?.tcProductArticleNo ||
      product?.tcProductNo ||
      product?.atcProductNo ||
      product?.lotNo ||
      product?.productNumber ||
      ""
    ).trim();
    if (!articleNo) return;

    const netWeight = toNumberOrZero(
      product?.tcProductNetShippingWeight ??
      product?.netShippingWeight ??
      product?.totalCertifiedWeight
    );
    const supplementaryWeight = toNumberOrZero(product?.tcProductSupplementaryWeight ?? product?.supplementaryWeight);
    const additivesWeight = toNumberOrZero(product?.tcProductAdditivesWeight ?? product?.additivesWeight);
    const claimStandards = normalizeStandardValues(
      (product?.tcProductCertifiedWeightInformation || [])
        .map((item: any) => standardCodeFromValue(item?.tcProductClaimsCategory))
    );
    const productStandards = claimStandards.length > 0
      ? claimStandards
      : normalizeStandardValues(
          product?.standards ||
          product?.standard ||
          product?.tcProductStandard ||
          product?.tcProductStandardInformation?.map((item: any) => standardCodeFromValue(item?.tcProductStandardLabelGrade))
        );

    if (productStandards.length > 0) {
      standardsByArticle[articleNo] = Array.from(new Set([...(standardsByArticle[articleNo] || []), ...productStandards]));
      standards.push(...productStandards);
    }
    if (netWeight > 0) {
      maxWeightByArticle[articleNo] = Math.max(maxWeightByArticle[articleNo] || 0, netWeight);
    }

    const rawMaterials = Array.isArray(product?.tcProductRawMaterialInformation)
      ? product.tcProductRawMaterialInformation
      : Array.isArray(product?.rawMaterialInformation)
      ? product.rawMaterialInformation
      : [product];

    rawMaterials.forEach((rm: any) => {
      const rawMaterialCode = extractLeadingCode(
        rm?.tcProductRawMaterialCode ||
        rm?.rawMaterialCode ||
        rm?.certifiedMaterial ||
        rm?.tcProductRawMaterialCodeStandardLabel ||
        rm?.tcProductIncomingCertifiedRawMaterialCode
      );
      const sourceRow: IncomingRawMaterialSource = {
        tcProductCategoryCode: extractLeadingCode(product?.tcProductCategoryCode || product?.atcProductCategoryCode || product?.productCategory),
        tcProductDetailCode: extractLeadingCode(product?.tcProductDetailCode || product?.atcProductDetailCode || product?.productDetail),
        rawMaterialCode,
        rawMaterialPercentage: extractPercentage({
          rawMaterialPercentage: rm?.tcProductRawMaterialPercentage ?? rm?.rawMaterialPercentage ?? rm?.rawMaterialCompostion ?? rm?.certifiedMaterialComposition,
        }),
        standard: productStandards[0] || "",
        isCertified: rm?.isCertified ?? rm?.is_certified ?? "Y",
        tcProductNetShippingWeight: netWeight,
        tcProductSupplementaryWeight: supplementaryWeight,
        tcProductAdditivesWeight: additivesWeight,
      };
      rawMaterialsByArticle[articleNo] = rawMaterialsByArticle[articleNo] || [];
      addUniqueRawMaterial(rawMaterialsByArticle[articleNo], sourceRow);
      sourceByArticle[articleNo] = sourceByArticle[articleNo] || sourceRow;
    });

    if (!rawMaterialsByArticle[articleNo]?.length) {
      const sourceRow: IncomingRawMaterialSource = {
        tcProductCategoryCode: extractLeadingCode(product?.tcProductCategoryCode || product?.atcProductCategoryCode || product?.productCategory),
        tcProductDetailCode: extractLeadingCode(product?.tcProductDetailCode || product?.atcProductDetailCode || product?.productDetail),
        rawMaterialCode: "",
        rawMaterialPercentage: 100,
        standard: productStandards[0] || "",
        tcProductNetShippingWeight: netWeight,
        tcProductSupplementaryWeight: supplementaryWeight,
        tcProductAdditivesWeight: additivesWeight,
      };
      sourceByArticle[articleNo] = sourceByArticle[articleNo] || sourceRow;
    }
  });

  return {
    standards: Array.from(new Set(standards)),
    standardsByArticle,
    sourceByArticle,
    rawMaterialsByArticle,
    maxWeightByArticle,
  };
};

export type SourceArticleData = {
  maps: ReturnType<typeof buildIncomingArticleMaps>;
  standards: string[];
  productOptions: Option[];
};

export const fetchSourceArticleMaps = async (
  executeGet: (url: string, params?: Record<string, any>) => Promise<any>,
  params: { type: string; tcId?: string; atcId?: string }
): Promise<SourceArticleData | null> => {
  const type = String(params.type || "").toUpperCase();
  const sourceId = params.tcId || params.atcId;
  if (!sourceId || !type) return null;

  const response = await executeGet(TCAppConfig.GET_INCOMING_TC_INFO, {
    incomingTcNoTcId: sourceId,
    incomingTcType: type,
  });
  const rawData = response?.data?.response ?? response?.data;

  if (type === "RMDF") {
    const items = Array.isArray(rawData?.reclaimedMaterialDetails)
      ? rawData.reclaimedMaterialDetails
      : Array.isArray(rawData?.reclaimedMaterials)
      ? rawData.reclaimedMaterials
      : unwrapRmdfLookupItems(rawData);
    if (items.length > 0) {
      const maps = buildRmdfArticleMaps(items);
      const productOptions: Option[] = Object.keys(maps.standardsByArticle).map((art) => ({
        value: art,
        label: art,
      }));
      return { maps, standards: ["RMDF"], productOptions };
    }
  }

  const products = unwrapIncomingProducts(rawData);
  const maps = buildIncomingArticleMaps(products);
  const productOptions: Option[] = products
    .map((product: any) => {
      const article = String(product?.tcProductArticleNo || product?.tcProductNo || product?.atcProductNo || "").trim();
      return { value: article, label: article };
    })
    .filter(
      (item: Option, index: number, self: Option[]) =>
        Boolean(item.value) && index === self.findIndex((other) => other.value === item.value)
    );

  return { maps, standards: maps.standards, productOptions };
};

const normalizeTeIdNumber = (raw?: string): string => {
  if (!raw) return "";
  const s = String(raw).trim();
  if (s.includes("-")) {
    const parts = s.split("-");
    for (let i = 0; i < parts.length; i++) {
      if (parts[i].toUpperCase() === "SC" && i + 1 < parts.length) {
        return parts[i + 1].replace(/^TE-?/i, "").replace(/^0+/, "");
      }
    }
  }
  return s.replace(/^TE-?/i, "").replace(/^0+/, "");
};

const extractTeIdFromFpfNo = (fpfNo: string): string => {
  if (!fpfNo) return "";
  const cleanStr = String(fpfNo).trim();
  const parts = cleanStr.split("-");
  const fpfIndex = parts.findIndex((p) => p.toUpperCase() === "FPF");
  if (fpfIndex !== -1) {
    for (let i = fpfIndex + 1; i < parts.length; i++) {
      const part = parts[i].trim();
      const upper = part.toUpperCase();
      if (!part || upper === "TE") continue;

      if (/\d/.test(part)) {
        const cleaned = part.replace(/^TE-?/i, "").replace(/^0+/, "");
        if (cleaned.length > 0) {
          return cleaned;
        }
      }
    }
  }

  const match = cleanStr.match(/FPF-(?:[A-Z0-9]+-)?(?:TE-?)?0*([1-9]\d*)/i);
  if (match && match[1]) {
    return match[1];
  }

  return "";
};

const extractTeIdFromRmdfNo = (rmdfNo: string): string => {
  if (!rmdfNo) return "";
  const cleanStr = String(rmdfNo).trim();
  const parts = cleanStr.split("-");
  const rmdfIndex = parts.findIndex((p) => ["RMDF", "MDF"].includes(p.toUpperCase()));
  if (rmdfIndex !== -1) {
    for (let i = rmdfIndex + 1; i < parts.length; i++) {
      const part = parts[i].trim();
      const upper = part.toUpperCase();
      if (!part || upper === "TE") continue;

      if (/\d/.test(part)) {
        const cleaned = part.replace(/^TE-?/i, "").replace(/^0+/, "");
        if (cleaned.length > 0) {
          return cleaned;
        }
      }
    }
  }

  const match = cleanStr.match(/(?:RMDF|MDF)-(?:[A-Z0-9]+-)?(?:TE-?)?0*([1-9]\d*)/i);
  if (match && match[1]) {
    return match[1];
  }

  return "";
};

let cachedLicensingCodes: Option[] | null = null;
let cachedCertificateTypes: Option[] | null = null;

const IncomingTcInfo: React.FC<IncomingTcInfoProps> = ({
  componentData,
  setComponentData,
  formRef,
  onValidationChange,
}) => {
  const { getCurrentFormValues: getTransactionDetailsFormValues } =
    useFormActions("Transaction-Details");
  const { getCurrentFormValues: getBuyerSellerFormValues } =
    useFormActions("buyer-seller-products");
  const { cbLicensingCode: loggedInCbLicensingCode } = useTransactionCertificateStore();
  const { executePost } = useApi();
  const { notify } = useNotification();

  const transactionDetails = getTransactionDetailsFormValues() || {};
  const isReturn = transactionDetails.tcIsReturn === "Yes";
  const returnTcId = transactionDetails.returnTcId;
  const returnTcNo = transactionDetails.returnTcNo;

  const returnTcSourceVersion = transactionDetails.returnTcSourceVersion || "";
  const isV4 = String(returnTcSourceVersion).toLowerCase().includes("v4") || String(returnTcSourceVersion).toLowerCase().includes("atu");
  const extractedLicensing = returnTcNo ? (returnTcNo.split("-")[0] || "") : "";
  const licenseValue = extractedLicensing ? (extractedLicensing.startsWith("CB-") ? extractedLicensing : `CB-${extractedLicensing}`) : "";

  const [productOptions, setProductOptions] = useState<Option[]>([]);
  const [incomingProductOptions, setIncomingProductOptions] = useState<Option[]>([]);
  const [incomingStandards, setIncomingStandards] = useState<string[]>([]);
  const [disableIncomingProduct, setDisableIncomingProduct] = useState(true);
  const [loadingStandards, setLoadingStandards] = useState(false);
  const [activeSearchField, setActiveSearchField] = useState<"tc" | "atc" | "rmdf" | "fpf" | null>(isReturn ? "tc" : null);
  const [licensingCodes, setLicensingCodes] = useState<Option[]>(cachedLicensingCodes || []);
  const [selectedCertType, setSelectedCertType] = useState<string>(isReturn ? "TC" : "");
  const [selectedLicensingCode, setSelectedLicensingCode] = useState<string>(isReturn ? licenseValue : "");
  const [productIncomingTcNo, setProductIncomingTcNo] = useState<string>(isReturn ? returnTcNo : "");
  const [incomingV4IssuedState, setIncomingV4IssuedState] = useState<IncomingV4IssuedFlag>(
    isReturn ? (isV4 ? "Y" : "N") : normalizeIncomingV4IssuedFlag(componentData.tcProductIncomingTcV4Issued)
  );

  const [certificateTypeOptions, setCertificateTypeOptions] = useState<Option[]>(cachedCertificateTypes || []);
  const [sourceLookupError, setSourceLookupError] = useState("");

  // Security Verification Questionnaire State for Incoming TC / ATU / GOTS / RMDF / FPF
  const [authSellerTeId, setAuthSellerTeId] = useState<string>("");
  const [authBuyerCountry, setAuthBuyerCountry] = useState<string>("");
  const [authTcStandards, setAuthTcStandards] = useState<string[]>([]);
  const [authBuyerTeId, setAuthBuyerTeId] = useState<string>(""); // For RMDF: Buyer Certified Org TE-ID
  const [authCertifiedOrgTeId, setAuthCertifiedOrgTeId] = useState<string>(""); // For FPF: Certified Org TE-ID
  const [authScNo, setAuthScNo] = useState<string>(""); // For FPF: Scope Certificate No

  const hasExistingIncomingSource = Boolean(
    isReturn ||
    componentData.tcIncomingTcId ||
    componentData.tcIncomingAtcId ||
    componentData.tcIncomingFpfId
  );
  const [isValidatingTc, setIsValidatingTc] = useState<boolean>(false);
  const [isValidated, setIsValidated] = useState<boolean>(hasExistingIncomingSource);

  useEffect(() => {
    onValidationChange?.(isValidated);
  }, [isValidated, onValidationChange]);
  const [validationError, setValidationError] = useState<string>("");
  const [countryOptionsList, setCountryOptionsList] = useState<{ label: string; value: string }[]>([]);
  const [sellerTeIdOptions, setSellerTeIdOptions] = useState<{ label: string; value: string }[]>([]);
  const [isFetchingTeIds, setIsFetchingTeIds] = useState<boolean>(false);
  const [selectedOptionObj, setSelectedOptionObj] = useState<{ id: string; type: IncomingSourceType; option: Option } | null>(null);

  const renderFriendlyValidationError = (errorText: string) => {
    if (!errorText) return null;

    let cleanText = errorText;
    let title = "Authentication Failed";
    const prefixMatch = cleanText.match(/^(Authentication failed|Validation failed|Verification failed):\s*/i);
    if (prefixMatch) {
      title = prefixMatch[1];
      cleanText = cleanText.substring(prefixMatch[0].length);
    }

    const rawItems = cleanText
      .split(/;\s*|\n+/)
      .map((item) => item.trim().replace(/^\.\s*/, ""))
      .filter((item) => item.length > 0);

    if (rawItems.length <= 1) {
      return (
        <Alert
          severity="error"
          sx={{
            borderRadius: "8px",
            border: "1px solid #fecaca",
            backgroundColor: "#fef2f2",
            "& .MuiAlert-icon": { color: "#dc2626" },
            "& .MuiAlert-message": { width: "100%", color: "#991b1b" },
          }}
        >
          <AlertTitle sx={{ fontWeight: 700, fontSize: "0.925rem", mb: 0.25, color: "#991b1b" }}>
            {title}
          </AlertTitle>
          <Typography variant="body2" sx={{ fontSize: "0.85rem", lineHeight: 1.5, color: "#7f1d1d" }}>
            {rawItems[0] || errorText}
          </Typography>
        </Alert>
      );
    }

    return (
      <Alert
        severity="error"
        sx={{
          borderRadius: "8px",
          border: "1px solid #fecaca",
          backgroundColor: "#fef2f2",
          "& .MuiAlert-icon": { color: "#dc2626", pt: "4px" },
          "& .MuiAlert-message": { width: "100%", color: "#991b1b" },
        }}
      >
        <AlertTitle sx={{ fontWeight: 700, fontSize: "0.925rem", mb: 0.5, color: "#991b1b" }}>
          {title}
        </AlertTitle>
        <Typography variant="body2" sx={{ fontSize: "0.825rem", fontWeight: 600, color: "#7f1d1d", mb: 0.75 }}>
          The following details do not match the certificate records:
        </Typography>
        <Box
          component="ul"
          sx={{
            m: 0,
            pl: 2.25,
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
          }}
        >
          {rawItems.map((item, idx) => (
            <Box
              component="li"
              key={idx}
              sx={{
                fontSize: "0.825rem",
                lineHeight: 1.45,
                color: "#7f1d1d",
              }}
            >
              {item}
            </Box>
          ))}
        </Box>
      </Alert>
    );
  };

  // Load dynamic Seller TE-ID options from API
  const fetchSellerTeIdOptions = async (query: string = "") => {
    setIsFetchingTeIds(true);
    try {
      const response = await buyerSellerDetailsService.getTEDetails({
        query_param: query,
        offset: 0,
      });
      const data = response?.data || [];
      if (Array.isArray(data)) {
        const formatted = data
          .map((item: any) => {
            const teId =
              item?.TE_ID ||
              item?.teId ||
              item?.te_id ||
              item?.code_1 ||
              (typeof item === "string" ? item : "");
            const name = item?.name || item?.account_name || item?.description_1 || "";
            if (!teId) return null;
            const strTeId = String(teId).trim();
            const strName = String(name).trim();
            return {
              value: strTeId,
              label: strTeId,
            };
          })
          .filter(Boolean) as { label: string; value: string }[];

        const uniqueMap = new Map<string, { label: string; value: string }>();
        formatted.forEach((item) => uniqueMap.set(item.value, item));
        setSellerTeIdOptions(Array.from(uniqueMap.values()));
      }
    } catch (err) {
      console.error("Error fetching Seller TE-ID options:", err);
    } finally {
      setIsFetchingTeIds(false);
    }
  };

  useEffect(() => {
    fetchSellerTeIdOptions("");
  }, []);

  // Load static country dropdown options
  useEffect(() => {
    try {
      const countryCodesData = require("../../../../../../../shared/data/country-codes.json");
      const transformedData = countryCodesData
        .map((country: any) => ({
          value: country.code,
          label: `${country.name} (${country.code})`,
        }))
        .sort((a: any, b: any) => a.label.localeCompare(b.label));
      setCountryOptionsList(transformedData);
    } catch (err) {
      console.error("Error loading static country codes:", err);
    }
  }, []);

  // For FPF and RMDF, force CB licensing code to be the same as the CB creating the TC
  useEffect(() => {
    if ((selectedCertType === "FPF" || selectedCertType === "RMDF") && loggedInCbLicensingCode) {
      const formattedCode = loggedInCbLicensingCode.startsWith("CB-")
        ? loggedInCbLicensingCode
        : `CB-${loggedInCbLicensingCode}`;
      setSelectedLicensingCode(formattedCode);
      formRef?.current?.setFieldValue("licensingCode", formattedCode);
      setComponentData((prev: any) => ({
        ...prev,
        incomingTcLicensingCode: formattedCode.replace(/^CB-/, ""),
      }));
    }
  }, [selectedCertType, loggedInCbLicensingCode]);

  const resetQuestionnaireState = () => {
    setAuthSellerTeId("");
    setAuthBuyerCountry("");
    setAuthTcStandards([]);
    setAuthBuyerTeId("");
    setAuthCertifiedOrgTeId("");
    setAuthScNo("");
    setValidationError("");
  };

  const handleValidateAndProceedIncomingTc = async () => {
    if (!productIncomingTcNo || !selectedLicensingCode) {
      setValidationError("Please select an incoming document number and licensing code.");
      return;
    }

    if (selectedCertType === "RMDF") {
      const buyerSellerFormValues = getBuyerSellerFormValues() || {};
      const sellerTeIdRaw = buyerSellerFormValues.sellerTEId || transactionDetails.sellerTeId || transactionDetails.tcSellerTeId || "";
      const sellerScNoRaw = buyerSellerFormValues.sellerSCNumber || transactionDetails.sellerSCNumber || transactionDetails.tcSellerScNo || "";
      const expectedSellerTeId = normalizeTeIdNumber(sellerTeIdRaw) || normalizeTeIdNumber(sellerScNoRaw);
      const rmdfTeId = extractTeIdFromRmdfNo(productIncomingTcNo);

      if (!expectedSellerTeId) {
        const errorMsg = "Seller SC Certified Organization TE-ID is required in the Buyer and Seller Information tab before attaching RMDF.";
        setValidationError(errorMsg);
        notify(errorMsg, "error");
        return;
      }

      if (!rmdfTeId || rmdfTeId !== expectedSellerTeId) {
        const displaySellerTeId = sellerTeIdRaw || `TE-00${expectedSellerTeId}`;
        const displayRmdfTeId = rmdfTeId ? (rmdfTeId.startsWith("TE-") ? rmdfTeId : `TE-00${rmdfTeId}`) : productIncomingTcNo;
        const errorMsg = `RMDF TE-ID (${displayRmdfTeId}) must match with the Seller SC TE-ID (${displaySellerTeId})`;
        setValidationError(errorMsg);
        notify(errorMsg, "error");
        return;
      }
    } else if (selectedCertType === "FPF") {
      const buyerSellerFormValues = getBuyerSellerFormValues() || {};
      const sellerTeIdRaw = buyerSellerFormValues.sellerTEId || transactionDetails.sellerTeId || transactionDetails.tcSellerTeId || "";
      const sellerScNoRaw = buyerSellerFormValues.sellerSCNumber || transactionDetails.sellerSCNumber || transactionDetails.tcSellerScNo || "";
      const expectedSellerTeId = normalizeTeIdNumber(sellerTeIdRaw) || normalizeTeIdNumber(sellerScNoRaw);
      const fpfTeId = extractTeIdFromFpfNo(productIncomingTcNo);

      if (!expectedSellerTeId) {
        const errorMsg = "Seller SC Certified Organization TE-ID is required in the Buyer and Seller Information tab before attaching FPF.";
        setValidationError(errorMsg);
        notify(errorMsg, "error");
        return;
      }

      if (!fpfTeId || fpfTeId !== expectedSellerTeId) {
        const displaySellerTeId = sellerTeIdRaw || `TE-00${expectedSellerTeId}`;
        const displayFpfTeId = fpfTeId ? (fpfTeId.startsWith("TE-") ? fpfTeId : `TE-00${fpfTeId}`) : productIncomingTcNo;
        const errorMsg = `FPF TE-ID (${displayFpfTeId}) must match with the Seller SC TE-ID (${displaySellerTeId})`;
        setValidationError(errorMsg);
        notify(errorMsg, "error");
        return;
      }
    } else {
      if (!authSellerTeId || !authBuyerCountry || authTcStandards.length === 0) {
        setValidationError("Please answer all security questions before validating.");
        return;
      }
    }

    setIsValidatingTc(true);
    setValidationError("");

    try {
      const activeCertType = selectedCertType || (incomingV4IssuedState === "Y" ? "TC V4" : "TC V3.1");
      let payload: Record<string, any>;

      if (selectedCertType === "RMDF") {
        const buyerSellerFormValues = getBuyerSellerFormValues() || {};
        const sellerTeIdRaw = buyerSellerFormValues.sellerTEId || transactionDetails.sellerTeId || transactionDetails.tcSellerTeId || "";
        const sellerScNoRaw = buyerSellerFormValues.sellerSCNumber || transactionDetails.sellerSCNumber || transactionDetails.tcSellerScNo || "";
        const expectedSellerTeId = normalizeTeIdNumber(sellerTeIdRaw) || normalizeTeIdNumber(sellerScNoRaw);
        const displaySellerTeId = sellerTeIdRaw || (expectedSellerTeId ? `TE-00${expectedSellerTeId}` : "");

        payload = {
          tcNo: productIncomingTcNo.trim(),
          tcCbLicensingCode: selectedLicensingCode,
          sellerTeId: displaySellerTeId,
          buyerTeId: displaySellerTeId,
          buyerCertifiedOrganizationTeId: displaySellerTeId,
          buyerCountry: "N/A",
          tcStandards: ["RMDF"],
          certType: "RMDF",
          version: "V2",
        };
      } else if (selectedCertType === "FPF") {
        const buyerSellerFormValues = getBuyerSellerFormValues() || {};
        const sellerTeIdRaw = buyerSellerFormValues.sellerTEId || transactionDetails.sellerTeId || transactionDetails.tcSellerTeId || "";
        const sellerScNoRaw = buyerSellerFormValues.sellerSCNumber || transactionDetails.sellerSCNumber || transactionDetails.tcSellerScNo || "";
        const expectedSellerTeId = normalizeTeIdNumber(sellerTeIdRaw) || normalizeTeIdNumber(sellerScNoRaw);
        const displaySellerTeId = sellerTeIdRaw || (expectedSellerTeId ? `TE-00${expectedSellerTeId}` : "");

        payload = {
          tcNo: productIncomingTcNo.trim(),
          tcCbLicensingCode: selectedLicensingCode,
          sellerTeId: displaySellerTeId,
          certifiedOrgTeId: displaySellerTeId,
          certifiedOrganizationTeId: displaySellerTeId,
          scNo: sellerScNoRaw || "",
          certifiedOrganizationScNo: sellerScNoRaw || "",
          buyerCountry: "N/A",
          tcStandards: ["FPF"],
          certType: "FPF",
          version: "V1",
        };
      } else {
        payload = {
          tcNo: productIncomingTcNo.trim(),
          tcCbLicensingCode: selectedLicensingCode,
          sellerTeId: authSellerTeId.trim(),
          buyerCountry: authBuyerCountry,
          tcStandards: authTcStandards,
          certType: activeCertType,
          version: incomingV4IssuedState === "Y" ? "V4" : "V3",
        };
      }

      const cbCodeVariants = Array.from(
        new Set([
          selectedLicensingCode.trim(),
          selectedLicensingCode.trim().startsWith("CB-")
            ? selectedLicensingCode.trim().replace(/^CB-/i, "")
            : `CB-${selectedLicensingCode.trim()}`,
        ])
      ).filter(Boolean);

      const primaryVersion = payload.version || (incomingV4IssuedState === "Y" ? "V4" : "V3");
      const versionVariants = Array.from(
        new Set([
          primaryVersion,
          primaryVersion === "V4" ? "V3" : "V4",
        ])
      ).filter(Boolean);

      console.group("🔍 Questionnaire Verification Debugger");
      console.log("📥 Selected Cert Type:", selectedCertType);
      console.log("📥 Incoming Document No:", productIncomingTcNo);
      console.log("📥 Selected Licensing Code:", selectedLicensingCode);
      console.log("📥 UI Submitted Answers:", {
        authSellerTeId,
        authBuyerCountry,
        authTcStandards,
        authCertifiedOrgTeId,
        authScNo,
      });
      console.log("📤 Initial Payload:", payload);
      console.log("⚙️ Testing CB Code Variants:", cbCodeVariants);
      console.log("⚙️ Testing Version Variants:", versionVariants);

      let response: any = null;
      let resBody: any = null;
      let isSuccess = false;

      outerLoop: for (const ver of versionVariants) {
        for (const cbCode of cbCodeVariants) {
          const currentPayload = {
            ...payload,
            tcCbLicensingCode: cbCode,
            version: ver,
          };
          console.log(`📡 Sending API Payload (ver: ${ver}, cbCode: ${cbCode}):`, currentPayload);
          try {
            try {
              response = await executePost(TCAppConfig.POST_VALIDATE_TC_ACCESS_V4, currentPayload);
            } catch (postErr: any) {
              const is404 = postErr?.response?.status === 404 || postErr?.status === 404 || String(postErr?.message || "").includes("404");
              if (is404) {
                try {
                  response = await executePost(`v1/tc/validate-tc-access-v4`, currentPayload);
                } catch (postErr2: any) {
                  const is404_2 = postErr2?.response?.status === 404 || postErr2?.status === 404 || String(postErr2?.message || "").includes("404");
                  if (is404_2) {
                    response = await executePost(`v1/tc/validate-tc-access`, currentPayload);
                  } else {
                    throw postErr2;
                  }
                }
              } else {
                throw postErr;
              }
            }
            resBody = response?.data ?? response;
            isSuccess =
              !resBody?.detail &&
              resBody?.status !== "error" &&
              !resBody?.error &&
              (resBody?.status === "success" ||
               resBody?.success === true ||
               resBody?.isValid === true ||
               resBody?.isVerified === true);
            
            console.log(`📩 Backend API Response (ver: ${ver}, cbCode: ${cbCode}):`, {
              httpStatus: response?.status,
              resBody,
              isSuccess,
            });

            if (isSuccess) break outerLoop;
          } catch (attemptErr: any) {
            console.warn(`⚠️ Attempt Failed (ver: ${ver}, cbCode: ${cbCode}):`, attemptErr);
          }
        }
      }

      if (isSuccess) {
        console.log("✅ Security Verification Matched Successfully!", {
          activePayload: payload,
          matchedResBody: resBody,
        });
        console.groupEnd();
        if (selectedCertType === "FPF") {
          setComponentData((prev: any) => ({
            ...prev,
            certifiedOrganizationTeId: authCertifiedOrgTeId.trim(),
            certifiedOrgTeId: authCertifiedOrgTeId.trim(),
            sellerTeId: authCertifiedOrgTeId.trim(),
            scNo: authScNo.trim(),
            certifiedOrganizationScNo: authScNo.trim(),
          }));
        }
        setIsValidated(true);
        setValidationError("");
        notify("Incoming document verified successfully!", "success");

        const targetId = selectedOptionObj?.id || componentData.tcIncomingTcId || componentData.tcIncomingAtcId || componentData.tcIncomingFpfId;
        const targetType: IncomingSourceType = selectedOptionObj?.type || (componentData.tcIncomingType as IncomingSourceType) || (selectedCertType === "GOTS" ? "GOTS" : selectedCertType === "ATU" ? "ATU" : selectedCertType === "FPF" ? "FPF" : selectedCertType === "RMDF" ? "RMDF" : "V4");

        if (targetId && targetType) {
          if (targetType === "GOTS" || targetType === "ATU") {
            await fetchIncomingATCDetails(targetId, targetType);
          } else {
            await fetchIncomingProductArticles(targetId, targetType);
          }
        }
      } else {
        const errorMsg =
          resBody?.message ||
          resBody?.detail ||
          resBody?.error ||
          "Authentication failed: Provided security details do not match the certificate.";
        console.error("❌ Security Verification Failed:", { errorMsg, resBody });
        console.groupEnd();
        setValidationError(errorMsg);
      }
    } catch (err: any) {
      const errorMsg =
        err?.response?.data?.message ||
        err?.response?.data?.detail ||
        err?.data?.detail ||
        err?.message ||
        "Authentication failed: Provided security details do not match the certificate.";
      console.error("❌ Security Verification Exception:", { errorMsg, err });
      console.groupEnd();
      setValidationError(errorMsg);
    } finally {
      setIsValidatingTc(false);
    }
  };

  useEffect(() => {
    if (isReturn && returnTcId && returnTcNo) {
      const returnTcSourceVersion = transactionDetails.returnTcSourceVersion || "";
      const isV4 = String(returnTcSourceVersion).toLowerCase().includes("v4") || String(returnTcSourceVersion).toLowerCase().includes("atu");
      const returnSourceType: IncomingSourceType = isV4 ? "V4" : "V3";
      const extractedLicensing = returnTcNo.split("-")[0] || "";
      const licenseValue = extractedLicensing.startsWith("CB-") ? extractedLicensing : `CB-${extractedLicensing}`;

      if (formRef?.current) {
        formRef.current.setFieldValue("tcProductIncomingTcV4Issued", isV4 ? "Y" : "N");
        formRef.current.setFieldValue("certificateType", "TC");
        formRef.current.setFieldValue("licensingCode", licenseValue);
        formRef.current.setFieldValue("searchCertificateNumber", returnTcNo);
        formRef.current.setFieldValue("tcIncomingTcId", returnTcId);
        formRef.current.setFieldValue("tcIncomingTCNo", returnTcNo);
      }

      if (
        componentData.tcIncomingTcId !== returnTcId ||
        componentData.tcIncomingTCNo !== returnTcNo ||
        componentData.licensingCode !== licenseValue ||
        componentData.incomingTcLicensingCode !== extractedLicensing
      ) {
        setComponentData({
          ...componentData,
          tcProductIncomingTcV4Issued: isV4 ? "Y" : "N",
          certificateType: "TC",
          licensingCode: licenseValue,
          incomingTcLicensingCode: extractedLicensing,
          tcIncomingTCNo: returnTcNo,
          tcIncomingTcId: returnTcId,
          tcIncomingType: returnSourceType,
        });
      }

      const alreadyFetched = componentData.tcIncomingTcId === returnTcId &&
        Object.keys(componentData.incomingProductRawMaterialsByArticle || {}).length > 0;

      if (!alreadyFetched) {
        void fetchIncomingSourceDetails(returnTcId, returnSourceType);
      }
    }
  }, [isReturn, returnTcId, returnTcNo, formRef, componentData]);

  // Guards against async races: results from a superseded search/selection are
  // dropped instead of clobbering newer state (values "flickering" / being
  // cleared after landing on step 2 came from exactly these late resolves).
  const searchSeqRef = useRef(0);
  // Number the selection handler already fetched for; the hydration effect
  // must not re-run the whole search cascade for it.
  const lastSearchedRef = useRef<string>("");

  const { TE_ID, formtype } = useParams();
  const { executeGet } = useApi();
  const theme = useTheme();
  const hasSubmitTc: boolean = formtype === "submited-tc";

  const certificateTypes = useMemo(
    () => filterIncomingCertificateTypes(certificateTypeOptions, incomingV4IssuedState),
    [certificateTypeOptions, incomingV4IssuedState]
  );

  const fetchProductOptions = async () => {
    try {
      const response = await executeGet(TCAppConfig.GET_LIST_PRODUCT_INFO_V4, { tcId: TE_ID });
      const options: Option[] =
        (response?.data?.response || []).map((item: any) => ({
          value: item.tcProductArticleNo,
          label: item.tcProductArticleNo,
        })) || [];

      // remove duplicates
      const uniqueOptions = options.filter(
        (item, index, self) => index === self.findIndex((t) => t.value === item.value)
      );

      setProductOptions(uniqueOptions);
    } catch (err) {
      console.error("Failed to fetch product options:", err);
    }
  };

  const normalizeStandards = (arr: any): string[] => {
    if (!Array.isArray(arr)) return [];
    // Flatten nested arrays and convert values to strings
    if (typeof (Array.prototype as any).flat === "function") {
      return (arr as any).flat(Infinity).filter((v: any) => v !== null && v !== undefined).map(String);
    }
    return (arr as any).reduce((acc: any[], cur: any) => acc.concat(Array.isArray(cur) ? cur : [cur]), []).filter((v: any) => v !== null && v !== undefined).map(String);
  };

  const fetchIncomingSourceDetails = async (
    sourceId: string,
    sourceType: IncomingSourceType
  ) => {
    if (!sourceId || !sourceType) return;
    setLoadingStandards(true);
    const seq = searchSeqRef.current;

    try {
      const sourceData = await fetchSourceArticleMaps(executeGet, {
        type: sourceType,
        tcId: sourceId,
      });
      if (seq !== searchSeqRef.current || !sourceData) return;

      setSourceLookupError("");
      setIncomingStandards(sourceData.standards);
      formRef?.current?.setFieldValue("tcIncomingTCStandard", sourceData.standards);
      setIncomingProductOptions(sourceData.productOptions);
      setDisableIncomingProduct(sourceData.productOptions.length === 0);
      // Only the keys this fetch owns — the parent merges over its current
      // state, so nothing changed while the request was in flight is lost.
      setComponentData({
        tcIncomingType: sourceType,
        tcProductIncomingTcV4Issued: sourceType === "V4" ? "Y" : "N",
        tcIncomingTCStandard: sourceData.standards,
        incomingProductStandardsByArticle: sourceData.maps.standardsByArticle,
        incomingProductSourceByArticle: sourceData.maps.sourceByArticle,
        incomingProductRawMaterialsByArticle: sourceData.maps.rawMaterialsByArticle,
        incomingProductMaxWeightByArticle: sourceData.maps.maxWeightByArticle,
      });
      if (sourceData.productOptions.length === 0) {
        setSourceLookupError(
          `The current ${sourceType} source was found, but its Incoming TC information contains no product articles.`
        );
      }
    } catch (err) {
      console.error(`Failed to fetch ${sourceType} incoming source details:`, err);
      if (seq === searchSeqRef.current) {
        setIncomingStandards([]);
        setIncomingProductOptions([]);
        setDisableIncomingProduct(true);
        setComponentData({
          tcIncomingProductNo: [],
          tcIncomingTCStandard: [],
          incomingProductStandardsByArticle: {},
          incomingProductSourceByArticle: {},
          incomingProductRawMaterialsByArticle: {},
          incomingProductMaxWeightByArticle: {},
          __replaceMaps: true,
        });
        setSourceLookupError(
          `Unable to load Incoming TC information for the selected ${sourceType} source.`
        );
      }
    } finally {
      if (seq === searchSeqRef.current) {
        setLoadingStandards(false);
      }
    }
  };

  const fetchIncomingProductArticles = (
    sourceId: string,
    sourceType: IncomingSourceType
  ) => fetchIncomingSourceDetails(sourceId, sourceType);

  const fetchIncomingATCDetails = (
    sourceId: string,
    sourceType: IncomingSourceType
  ) => fetchIncomingSourceDetails(sourceId, sourceType);


  const getTCNoforSearch = async (
    value: string,
    offset: number,
    type: string,
    cbLicensingCode?: string
  ): Promise<Option[]> => {
    if(value === 'na') return [];
    try {
      const expectedSourceVersion = getIncomingTcSourceVersion(incomingV4IssuedState);
      const queryParams: Record<string, any> = {
        tcNo: type === "query" ? value : "",
        offset,
        name: type === "name" ? value : "",
        tcCbLicensingCode: cbLicensingCode,
        sourceVersion: expectedSourceVersion,
      };

      const response: any = await executeGet(TCAppConfig.GET_TC_NO_INPUT_REF, queryParams);
      // The function app wraps rows in {response: [...]}; the container proxy
      // may return the bare array. Accept both shapes.
      const rows = Array.isArray(response?.data)
        ? response.data
        : response?.data?.response || [];
      return rows
        .map((item: any) => ({
          label: item.tcNo,
          value: item.tcNo,
          tcId: item.tcId,
          sourceVersion: item.sourceVersion || item.tcSourceVersion || item.version || "",
        }))
        .filter((item: Option) =>
          String(item.sourceVersion || "").trim().toUpperCase() === expectedSourceVersion
        );
    } catch (e) {
      console.error(`Error fetching Input TC No: ${e}`);
      return [];
    }
  };

  const getInputATCNoForSearch = async (
    value: string,
    offset: number,
    type: string,
    cbLicensingCode?: string,
    requestedSourceType?: "ATU" | "GOTS"
  ): Promise<Option[]> => {
    try {
      const queryParams: Record<string, any> = {
        atcNo: type === "query" ? value : "",
        offset,
        name: type === "name" ? value : "",
        atcCbLicensingCode: cbLicensingCode,
      };

      let response: any = await executeGet(TCAppConfig.GET_ATC_NO, queryParams);
      let allResults = response.data?.response || [];

      if ((!allResults || allResults.length === 0) && cbLicensingCode) {
        const altCbCode = cbLicensingCode.startsWith("CB-")
          ? cbLicensingCode.replace(/^CB-/i, "")
          : `CB-${cbLicensingCode}`;
        const altParams = { ...queryParams, atcCbLicensingCode: altCbCode };
        try {
          const altResponse: any = await executeGet(TCAppConfig.GET_ATC_NO, altParams);
          if (altResponse.data?.response?.length) {
            allResults = altResponse.data.response;
          }
        } catch {
          // ignore fallback error
        }
      }
      
      // Type tokens may sit anywhere in the number (e.g. ATU_CUC_123 or
      // CUC_ATU_123), so match separator-split tokens, not just the prefix.
      const hasToken = (no: any, token: string) => {
        try {
          return String(no || '').toUpperCase().split(/[^A-Z0-9]+/).includes(token);
        } catch {
          return false;
        }
      };
      const sourceTypeToMatch = requestedSourceType || selectedCertType;
      const filteredResults = allResults.filter((item: any) => {
        if (sourceTypeToMatch === 'GOTS') {
          return hasToken(item.atcNo, 'GOTS');
        } else if (sourceTypeToMatch === 'ATU') {
          return hasToken(item.atcNo, 'ATU');
        }
        return true;
      });

      return filteredResults.map((item: any) => ({
        label: item.atcNo,
        value: item.atcNo,
        atcId: item.atcId,
      })) || [];
    } catch (e) {
      console.error(`Error fetching Input ATC No: ${e}`);
      return [];
    }
  };

  const getRMDFforSearch = async (
    value: string,
    offset: number,
    type: string,
    cbLicensingCode?: string
  ): Promise<Option[]> => {
    try {
      const buyerSellerFormValues = getBuyerSellerFormValues() || {};
      const sellerTeIdRaw = buyerSellerFormValues.sellerTEId || transactionDetails.sellerTeId || transactionDetails.tcSellerTeId || "";
      const sellerScNoRaw = buyerSellerFormValues.sellerSCNumber || transactionDetails.sellerSCNumber || transactionDetails.tcSellerScNo || "";
      const expectedSellerTeId = normalizeTeIdNumber(sellerTeIdRaw) || normalizeTeIdNumber(sellerScNoRaw);

      const queryParams: Record<string, any> = {
        rmdfNo: type === "query" ? value : "",
        offset,
        name: type === "name" ? value : "",
        tcCbLicensingCode: cbLicensingCode,
      };

      if (sellerTeIdRaw) {
        queryParams.teId = sellerTeIdRaw;
      }
      if (sellerScNoRaw) {
        queryParams.scNo = sellerScNoRaw;
      }

      const response: any = await executeGet(TCAppConfig.GET_RMDF_SEARCH, queryParams);
      const items = unwrapRmdfLookupItems(response?.data);

      const filteredItems = items.filter((item: any) => {
        const rmdfNo = item.tcEtcMdfNumber || item.rmdfMdfNumber || "";
        if (!expectedSellerTeId || !rmdfNo) return true;
        const rmdfTeId = extractTeIdFromRmdfNo(rmdfNo);
        if (!rmdfTeId) return true;
        return rmdfTeId === expectedSellerTeId;
      });

      return (
        filteredItems.map((item: any) => ({
          label: item.tcEtcMdfNumber || item.rmdfMdfNumber,
          value: item.tcEtcMdfNumber || item.rmdfMdfNumber,
          mdfId: item.mdfId,
        } as Option)) || []
      );
    } catch (e) {
      console.error(`Error fetching Input RMDF No: ${e}`);
      return [];
    }
  };

  const getFPFforSearch = async (
    value: string,
    offset: number,
    type: string,
    cbLicensingCode?: string
  ): Promise<Option[]> => {
    try {
      // Extract active Seller TE-ID from Buyer-Seller form or Transaction Details
      const buyerSellerFormValues = getBuyerSellerFormValues() || {};
      const sellerTeIdRaw = buyerSellerFormValues.sellerTEId || transactionDetails.sellerTeId || transactionDetails.tcSellerTeId || "";
      const sellerScNoRaw = buyerSellerFormValues.sellerSCNumber || transactionDetails.sellerSCNumber || transactionDetails.tcSellerScNo || "";
      const expectedSellerTeId = normalizeTeIdNumber(sellerTeIdRaw) || normalizeTeIdNumber(sellerScNoRaw);

      const queryParams: Record<string, any> = {
        fpfNo: type === "query" ? value : "",
        offset,
        tcCbLicensingCode: cbLicensingCode,
      };

      if (sellerTeIdRaw) {
        queryParams.teId = sellerTeIdRaw;
      }
      if (sellerScNoRaw) {
        queryParams.scNo = sellerScNoRaw;
      }

      const response: any = await executeGet(TCAppConfig.GET_FPF_SEARCH, queryParams);
      const items = response?.data?.response || response?.data || [];

      const filteredItems = items.filter((group: any) => {
        const fpfNo = group.tcEtcMdfNumber || group.fpfNumber || group.fpfNo || group.fpfNoId || "";
        if (!expectedSellerTeId || !fpfNo) return true;
        const fpfTeId = extractTeIdFromFpfNo(fpfNo);
        if (!fpfTeId) return true;
        return fpfTeId === expectedSellerTeId;
      });

      return (
        filteredItems.map((group: any) => {
          const fpfNo = group.tcEtcMdfNumber || group.fpfNumber || group.fpfNo || group.fpfNoId;
          return {
            label: fpfNo,
            value: fpfNo,
            fpfId: group.fpfId,
          } as Option;
        }) || []
      );
    } catch (e) {
      console.error(`Error fetching FPF No: ${e}`);
      return [];
    }
  };

  const handleCertificateNumberChange = (selectedOption: Option | null) => {
    const value = selectedOption?.value || null;
    setSourceLookupError("");

    if (!isReturn) {
      setIsValidated(selectedCertType === "RMDF" || selectedCertType === "FPF");
      resetQuestionnaireState();
    }

    // Preserve the licensing code from both state and parent data
    const licensingCodeToPreserve = selectedLicensingCode || 
      (componentData.incomingTcLicensingCode ? 
        (componentData.incomingTcLicensingCode.startsWith("CB-") ? 
          componentData.incomingTcLicensingCode.replace("CB-", "") : 
          componentData.incomingTcLicensingCode) 
        : "");
    
    if (selectedCertType === 'TC') {
      formRef?.current?.setFieldValue("tcIncomingTCNo", value);
      setProductIncomingTcNo(value || "");

      if (value) {
        resetIncomingFields();
        setSourceLookupError("");
        setActiveSearchField("tc");
        const seq = ++searchSeqRef.current;
        lastSearchedRef.current = buildIncomingSourceLookupKey("TC", selectedLicensingCode, value);
        const selectedPromise = selectedOption?.tcId
          ? Promise.resolve([selectedOption])
          : getTCNoforSearch(value, 0, "query", selectedLicensingCode);
        selectedPromise.then((res) => {
          if (seq !== searchSeqRef.current) return;
          const selected = res.find((r) => r.value === value);
          if (selected?.tcId) {
            setSourceLookupError("");
            formRef?.current?.setFieldValue("tcIncomingTcId", selected.tcId);
            if (licensingCodeToPreserve) {
              const codeWithPrefix = licensingCodeToPreserve.startsWith("CB-") ? licensingCodeToPreserve : `CB-${licensingCodeToPreserve}`;
              formRef?.current?.setFieldValue("licensingCode", codeWithPrefix);
              setSelectedLicensingCode(codeWithPrefix);
            }
            const incomingType: IncomingSourceType = String(selected.sourceVersion || "").toLowerCase().includes("v3") ? "V3" : "V4";
            setSelectedOptionObj({ id: selected.tcId, type: incomingType, option: selected });
            setComponentData({
              tcIncomingTCNo: value,
              tcIncomingTcId: selected.tcId,
              tcIncomingType: incomingType,
              tcProductIncomingTcV4Issued: incomingV4IssuedState,
              tcIncomingAtcId: "",
              tcIncomingLedgerTransactionId: "",
              tcIncomingProductNo: [],
              tcIncomingTCStandard: [],
              incomingProductStandardsByArticle: {},
              incomingProductSourceByArticle: {},
              incomingProductRawMaterialsByArticle: {},
              incomingProductMaxWeightByArticle: {},
              incomingTcLicensingCode: licensingCodeToPreserve
            });
            if (isReturn) {
              fetchIncomingProductArticles(selected.tcId, incomingType);
            }
          } else {
            clearResolvedIncomingSource("TC", value);
            if (licensingCodeToPreserve) {
              const codeWithPrefix = licensingCodeToPreserve.startsWith("CB-") ? licensingCodeToPreserve : `CB-${licensingCodeToPreserve}`;
              formRef?.current?.setFieldValue("licensingCode", codeWithPrefix);
              setSelectedLicensingCode(codeWithPrefix);
            }
            setComponentData({
              tcIncomingTCNo: value,
              tcIncomingTcId: "",
              tcIncomingType: "",
              tcProductIncomingTcV4Issued: incomingV4IssuedState,
              tcIncomingAtcId: "",
              tcIncomingLedgerTransactionId: "",
              tcIncomingProductNo: [],
              tcIncomingTCStandard: [],
              incomingProductStandardsByArticle: {},
              incomingProductSourceByArticle: {},
              incomingProductRawMaterialsByArticle: {},
              incomingProductMaxWeightByArticle: {},
              incomingTcLicensingCode: licensingCodeToPreserve
            });
          }
        });
      } else {
        setActiveSearchField(null);
        resetIncomingFields();
        searchSeqRef.current += 1;
        lastSearchedRef.current = "";
        formRef?.current?.setFieldValue("tcIncomingTcId", "");
        if (licensingCodeToPreserve) {
          const codeWithPrefix = licensingCodeToPreserve.startsWith("CB-") ? licensingCodeToPreserve : `CB-${licensingCodeToPreserve}`;
          formRef?.current?.setFieldValue("licensingCode", codeWithPrefix);
          setSelectedLicensingCode(codeWithPrefix);
        }
        setComponentData({
          tcIncomingTCNo: "",
          tcIncomingTcId: "",
          tcIncomingType: "",
          tcProductIncomingTcV4Issued: incomingV4IssuedState,
          tcIncomingAtcId: "",
          tcIncomingLedgerTransactionId: "",
          tcIncomingProductNo: [],
          tcIncomingTCStandard: [],
          incomingProductStandardsByArticle: {},
          incomingProductSourceByArticle: {},
          incomingProductRawMaterialsByArticle: {},
          incomingProductMaxWeightByArticle: {},
          incomingTcLicensingCode: licensingCodeToPreserve
        });
      }
    } else if (selectedCertType === 'RMDF') {
      formRef?.current?.setFieldValue("tcIncomingRMDFNo", value);
      setProductIncomingTcNo(value || "");
      
      if (value) {
        // Validate Seller TE-ID match for RMDF
        const buyerSellerFormValues = getBuyerSellerFormValues() || {};
        const sellerTeIdRaw = buyerSellerFormValues.sellerTEId || transactionDetails.sellerTeId || transactionDetails.tcSellerTeId || "";
        const sellerScNoRaw = buyerSellerFormValues.sellerSCNumber || transactionDetails.sellerSCNumber || transactionDetails.tcSellerScNo || "";
        const expectedSellerTeId = normalizeTeIdNumber(sellerTeIdRaw) || normalizeTeIdNumber(sellerScNoRaw);
        const rmdfTeId = extractTeIdFromRmdfNo(value);

        if (expectedSellerTeId && rmdfTeId && rmdfTeId !== expectedSellerTeId) {
          const displaySellerTeId = sellerTeIdRaw || `TE-00${expectedSellerTeId}`;
          const displayRmdfTeId = rmdfTeId.startsWith("TE-") ? rmdfTeId : `TE-00${rmdfTeId}`;
          notify(`RMDF TE-ID (${displayRmdfTeId}) must match the Seller Scope Certificate TE-ID (${displaySellerTeId})`, "error");
          setSourceLookupError(`RMDF TE-ID (${displayRmdfTeId}) must match the Seller Scope Certificate TE-ID (${displaySellerTeId})`);
          formRef?.current?.setFieldValue("searchCertificateNumber", "");
          formRef?.current?.setFieldValue("tcIncomingRMDFNo", "");
          setProductIncomingTcNo("");
          return;
        }

        resetIncomingFields();
        setSourceLookupError("");
        setActiveSearchField("rmdf");
        const seq = ++searchSeqRef.current;
        lastSearchedRef.current = buildIncomingSourceLookupKey("RMDF", selectedLicensingCode, value);
        const selectedPromise = selectedOption?.mdfId
          ? Promise.resolve([selectedOption])
          : getRMDFforSearch(value, 0, "query", selectedLicensingCode);
        selectedPromise.then((res) => {
          if (seq !== searchSeqRef.current) return;
          const selected = res.find((r) => r.value === value);
          const sourceId = selected?.mdfId || "";
          if (selected && sourceId) {
            setSourceLookupError("");
            formRef?.current?.setFieldValue("tcIncomingTcId", sourceId);
            formRef?.current?.setFieldValue("tcIncomingLedgerTransactionId", "");
            if (licensingCodeToPreserve) {
              const codeWithPrefix = licensingCodeToPreserve.startsWith("CB-") ? licensingCodeToPreserve : `CB-${licensingCodeToPreserve}`;
              formRef?.current?.setFieldValue("licensingCode", codeWithPrefix);
              setSelectedLicensingCode(codeWithPrefix);
            }
            setSelectedOptionObj({ id: sourceId, type: "RMDF", option: selected });
            setComponentData({
              tcIncomingRMDFNo: value,
              tcIncomingTcId: sourceId,
              tcIncomingLedgerTransactionId: "",
              tcIncomingType: "RMDF",
              tcProductIncomingTcV4Issued: "N",
              tcIncomingAtcId: "",
              tcIncomingProductNo: [],
              tcIncomingTCStandard: [],
              incomingProductStandardsByArticle: {},
              incomingProductSourceByArticle: {},
              incomingProductRawMaterialsByArticle: {},
              incomingProductMaxWeightByArticle: {},
              incomingTcLicensingCode: licensingCodeToPreserve
            });
            fetchIncomingProductArticles(sourceId, "RMDF");
          } else {
            clearResolvedIncomingSource("RMDF", value);
            if (licensingCodeToPreserve) {
              const codeWithPrefix = licensingCodeToPreserve.startsWith("CB-") ? licensingCodeToPreserve : `CB-${licensingCodeToPreserve}`;
              formRef?.current?.setFieldValue("licensingCode", codeWithPrefix);
              setSelectedLicensingCode(codeWithPrefix);
            }
            setComponentData({
              tcIncomingRMDFNo: value,
              tcIncomingLedgerTransactionId: "",
              tcIncomingType: "RMDF",
              tcProductIncomingTcV4Issued: "N",
              tcIncomingTcId: "",
              tcIncomingAtcId: "",
              tcIncomingProductNo: [],
              tcIncomingTCStandard: [],
              incomingProductStandardsByArticle: {},
              incomingProductSourceByArticle: {},
              incomingProductRawMaterialsByArticle: {},
              incomingProductMaxWeightByArticle: {},
              incomingTcLicensingCode: licensingCodeToPreserve
            });
          }
        });
      } else {
        setActiveSearchField(null);
        resetIncomingFields();
        searchSeqRef.current += 1;
        lastSearchedRef.current = "";
        formRef?.current?.setFieldValue("tcIncomingLedgerTransactionId", "");
        if (licensingCodeToPreserve) {
          const codeWithPrefix = licensingCodeToPreserve.startsWith("CB-") ? licensingCodeToPreserve : `CB-${licensingCodeToPreserve}`;
          formRef?.current?.setFieldValue("licensingCode", codeWithPrefix);
          setSelectedLicensingCode(codeWithPrefix);
        }
        setComponentData({
          tcIncomingRMDFNo: "",
          tcIncomingLedgerTransactionId: "",
          tcIncomingType: "",
          tcProductIncomingTcV4Issued: "N",
          tcIncomingTcId: "",
          tcIncomingAtcId: "",
          tcIncomingProductNo: [],
          tcIncomingTCStandard: [],
          incomingProductStandardsByArticle: {},
          incomingProductSourceByArticle: {},
          incomingProductRawMaterialsByArticle: {},
          incomingProductMaxWeightByArticle: {},
          incomingTcLicensingCode: licensingCodeToPreserve
        });
      }
    } else if (selectedCertType === 'FPF') {
      formRef?.current?.setFieldValue("tcIncomingFPFNo", value);
      setProductIncomingTcNo(value || "");

      if (value) {
        // Validate Seller TE-ID match for FPF
        const buyerSellerFormValues = getBuyerSellerFormValues() || {};
        const sellerTeIdRaw = buyerSellerFormValues.sellerTEId || transactionDetails.sellerTeId || transactionDetails.tcSellerTeId || "";
        const sellerScNoRaw = buyerSellerFormValues.sellerSCNumber || transactionDetails.sellerSCNumber || transactionDetails.tcSellerScNo || "";
        const expectedSellerTeId = normalizeTeIdNumber(sellerTeIdRaw) || normalizeTeIdNumber(sellerScNoRaw);
        const fpfTeId = extractTeIdFromFpfNo(value);

        if (expectedSellerTeId && fpfTeId && fpfTeId !== expectedSellerTeId) {
          const displaySellerTeId = sellerTeIdRaw || `TE-00${expectedSellerTeId}`;
          const displayFpfTeId = fpfTeId.startsWith("TE-") ? fpfTeId : `TE-00${fpfTeId}`;
          notify(`FPF Certified Organization TE-ID (${displayFpfTeId}) must match the Seller Scope Certificate TE-ID (${displaySellerTeId})`, "error");
          setSourceLookupError(`FPF Certified Organization TE-ID (${displayFpfTeId}) must match the Seller Scope Certificate TE-ID (${displaySellerTeId})`);
          formRef?.current?.setFieldValue("searchCertificateNumber", "");
          formRef?.current?.setFieldValue("tcIncomingFPFNo", "");
          setProductIncomingTcNo("");
          return;
        }

        resetIncomingFields();
        setSourceLookupError("");
        setActiveSearchField("fpf");
        const seq = ++searchSeqRef.current;
        lastSearchedRef.current = buildIncomingSourceLookupKey("FPF", selectedLicensingCode, value);
        const selectedPromise = selectedOption?.fpfId
          ? Promise.resolve([selectedOption])
          : getFPFforSearch(value, 0, "query", selectedLicensingCode);
        selectedPromise.then((res) => {
          if (seq !== searchSeqRef.current) return;
          const selected = res.find((r) => r.value === value);
          const sourceId = selected?.fpfId || "";
          if (selected && sourceId) {
            setSourceLookupError("");
            formRef?.current?.setFieldValue("tcIncomingTcId", sourceId);
            formRef?.current?.setFieldValue("tcIncomingLedgerTransactionId", "");
            if (licensingCodeToPreserve) {
              const codeWithPrefix = licensingCodeToPreserve.startsWith("CB-") ? licensingCodeToPreserve : `CB-${licensingCodeToPreserve}`;
              formRef?.current?.setFieldValue("licensingCode", codeWithPrefix);
              setSelectedLicensingCode(codeWithPrefix);
            }
            setSelectedOptionObj({ id: sourceId, type: "FPF", option: selected });
            setComponentData({
              tcIncomingFPFNo: value,
              tcIncomingTcId: sourceId,
              tcIncomingLedgerTransactionId: "",
              tcIncomingType: "FPF",
              tcProductIncomingTcV4Issued: "N",
              tcIncomingAtcId: "",
              tcIncomingTCNo: "",
              tcIncomingATCNo: "",
              tcIncomingRMDFNo: "",
              tcIncomingProductNo: [],
              tcIncomingTCStandard: [],
              tcIncomingFpfId: selected.fpfId || "",
              incomingProductStandardsByArticle: {},
              incomingProductSourceByArticle: {},
              incomingProductRawMaterialsByArticle: {},
              incomingProductMaxWeightByArticle: {},
              incomingTcLicensingCode: licensingCodeToPreserve
            });
            fetchIncomingProductArticles(sourceId, "FPF");
          } else {
            clearResolvedIncomingSource("FPF", value);
            if (licensingCodeToPreserve) {
              const codeWithPrefix = licensingCodeToPreserve.startsWith("CB-") ? licensingCodeToPreserve : `CB-${licensingCodeToPreserve}`;
              formRef?.current?.setFieldValue("licensingCode", codeWithPrefix);
              setSelectedLicensingCode(codeWithPrefix);
            }
            setComponentData({
              tcIncomingFPFNo: value,
              tcIncomingLedgerTransactionId: "",
              tcIncomingType: "FPF",
              tcProductIncomingTcV4Issued: "N",
              tcIncomingTcId: "",
              tcIncomingAtcId: "",
              tcIncomingTCNo: "",
              tcIncomingATCNo: "",
              tcIncomingRMDFNo: "",
              tcIncomingProductNo: [],
              tcIncomingTCStandard: [],
              tcIncomingFpfId: "",
              incomingProductStandardsByArticle: {},
              incomingProductSourceByArticle: {},
              incomingProductRawMaterialsByArticle: {},
              incomingProductMaxWeightByArticle: {},
              incomingTcLicensingCode: licensingCodeToPreserve
            });
          }
        });
      } else {
        setActiveSearchField(null);
        resetIncomingFields();
        searchSeqRef.current += 1;
        lastSearchedRef.current = "";
        formRef?.current?.setFieldValue("tcIncomingLedgerTransactionId", "");
        if (licensingCodeToPreserve) {
          const codeWithPrefix = licensingCodeToPreserve.startsWith("CB-") ? licensingCodeToPreserve : `CB-${licensingCodeToPreserve}`;
          formRef?.current?.setFieldValue("licensingCode", codeWithPrefix);
          setSelectedLicensingCode(codeWithPrefix);
        }
        setComponentData({
          tcIncomingFPFNo: "",
          tcIncomingLedgerTransactionId: "",
          tcIncomingType: "",
          tcProductIncomingTcV4Issued: "N",
          tcIncomingTcId: "",
          tcIncomingAtcId: "",
          tcIncomingTCNo: "",
          tcIncomingATCNo: "",
          tcIncomingRMDFNo: "",
          tcIncomingProductNo: [],
          tcIncomingTCStandard: [],
          tcIncomingFpfId: "",
          incomingProductStandardsByArticle: {},
          incomingProductSourceByArticle: {},
          incomingProductRawMaterialsByArticle: {},
          incomingProductMaxWeightByArticle: {},
          incomingTcLicensingCode: licensingCodeToPreserve
        });
      }
    } else if (selectedCertType === 'GOTS' || selectedCertType === 'ATU') {
      formRef?.current?.setFieldValue("tcIncomingATCNo", value);
      setProductIncomingTcNo(value || "");
      
      if (value) {
        resetIncomingFields();
        setSourceLookupError("");
        setActiveSearchField("atc");
        const seq = ++searchSeqRef.current;
        lastSearchedRef.current = buildIncomingSourceLookupKey(selectedCertType, selectedLicensingCode, value);
        const selectedPromise = selectedOption?.atcId
          ? Promise.resolve([selectedOption])
          : getInputATCNoForSearch(
              value,
              0,
              "query",
              selectedLicensingCode,
              selectedCertType === "GOTS" ? "GOTS" : "ATU"
            );
        selectedPromise.then((res) => {
          if (seq !== searchSeqRef.current) return;
          const selected = res.find((r) => r.value === value);
          if (selected?.atcId) {
            setSourceLookupError("");
            formRef?.current?.setFieldValue("tcIncomingTcId", selected.atcId);
            formRef?.current?.setFieldValue("tcIncomingAtcId", selected.atcId);
            if (licensingCodeToPreserve) {
              const codeWithPrefix = licensingCodeToPreserve.startsWith("CB-") ? licensingCodeToPreserve : `CB-${licensingCodeToPreserve}`;
              formRef?.current?.setFieldValue("licensingCode", codeWithPrefix);
              setSelectedLicensingCode(codeWithPrefix);
            }
            const sourceType: IncomingSourceType = selectedCertType === "GOTS" ? "GOTS" : "ATU";
            setSelectedOptionObj({ id: selected.atcId, type: sourceType, option: selected });
            setComponentData({
              tcIncomingATCNo: value,
              tcIncomingTcId: selected.atcId,
              tcIncomingAtcId: selected.atcId,
              tcIncomingType: sourceType,
              tcProductIncomingTcV4Issued: "N",
              tcIncomingLedgerTransactionId: "",
              tcIncomingProductNo: [],
              tcIncomingTCStandard: [],
              tcIncomingFpfId: "",
              incomingProductStandardsByArticle: {},
              incomingProductSourceByArticle: {},
              incomingProductRawMaterialsByArticle: {},
              incomingProductMaxWeightByArticle: {},
              incomingTcLicensingCode: licensingCodeToPreserve
            });
            if (isReturn) {
              fetchIncomingATCDetails(selected.atcId, sourceType);
            }
          } else {
            clearResolvedIncomingSource(selectedCertType === "GOTS" ? "GOTS" : "ATU", value);
            if (licensingCodeToPreserve) {
              const codeWithPrefix = licensingCodeToPreserve.startsWith("CB-") ? licensingCodeToPreserve : `CB-${licensingCodeToPreserve}`;
              formRef?.current?.setFieldValue("licensingCode", codeWithPrefix);
              setSelectedLicensingCode(codeWithPrefix);
            }
            setComponentData({
              tcIncomingATCNo: value,
              tcIncomingAtcId: "",
              tcIncomingType: selectedCertType === "GOTS" ? "GOTS" : "ATU",
              tcProductIncomingTcV4Issued: "N",
              tcIncomingTcId: "",
              tcIncomingLedgerTransactionId: "",
              tcIncomingProductNo: [],
              tcIncomingTCStandard: [],
              tcIncomingFpfId: "",
              incomingProductStandardsByArticle: {},
              incomingProductSourceByArticle: {},
              incomingProductRawMaterialsByArticle: {},
              incomingProductMaxWeightByArticle: {},
              incomingTcLicensingCode: licensingCodeToPreserve
            });
          }
        });
      } else {
        setActiveSearchField(null);
        resetIncomingFields();
        searchSeqRef.current += 1;
        lastSearchedRef.current = "";
        formRef?.current?.setFieldValue("tcIncomingAtcId", "");
        if (licensingCodeToPreserve) {
          const codeWithPrefix = licensingCodeToPreserve.startsWith("CB-") ? licensingCodeToPreserve : `CB-${licensingCodeToPreserve}`;
          formRef?.current?.setFieldValue("licensingCode", codeWithPrefix);
          setSelectedLicensingCode(codeWithPrefix);
        }
        setComponentData({
          tcIncomingATCNo: "",
          tcIncomingAtcId: "",
          tcIncomingType: "",
          tcProductIncomingTcV4Issued: "N",
          tcIncomingTcId: "",
          tcIncomingLedgerTransactionId: "",
          tcIncomingProductNo: [],
          tcIncomingTCStandard: [],
          tcIncomingFpfId: "",
          incomingProductStandardsByArticle: {},
          incomingProductSourceByArticle: {},
          incomingProductRawMaterialsByArticle: {},
          incomingProductMaxWeightByArticle: {},
          incomingTcLicensingCode: licensingCodeToPreserve
        });
      }
    }
  };

  const resetIncomingFields = () => {
    setDisableIncomingProduct(true);
    setIncomingProductOptions([]);
    setIncomingStandards([]);
    formRef?.current?.setFieldValue("tcIncomingTCStandard", []);
    formRef?.current?.setFieldValue("tcIncomingProductNo", []);
    formRef?.current?.setFieldValue("tcIncomingTcId", "");
    formRef?.current?.setFieldValue("tcIncomingAtcId", "");
    formRef?.current?.setFieldValue("tcIncomingLedgerTransactionId", "");
    formRef?.current?.setFieldValue("tcIncomingFpfId", "");
  };

  const clearResolvedIncomingSource = (
    sourceType: "TC" | "RMDF" | "FPF" | "ATU" | "GOTS",
    sourceNumber: string
  ) => {
    resetIncomingFields();
    setComponentData({
      tcIncomingTcId: "",
      tcIncomingAtcId: "",
      tcIncomingLedgerTransactionId: "",
      tcIncomingFpfId: "",
      tcIncomingType: sourceType === "TC" ? "" : sourceType,
      tcProductIncomingTcV4Issued:
        sourceType === "TC" ? incomingV4IssuedState : "N",
      tcIncomingProductNo: [],
      tcIncomingTCStandard: [],
      incomingProductStandardsByArticle: {},
      incomingProductSourceByArticle: {},
      incomingProductRawMaterialsByArticle: {},
      incomingProductMaxWeightByArticle: {},
      __replaceMaps: true,
    });
    setSourceLookupError(
      `No current valid ${sourceType} source was found for ${sourceNumber} and the selected CB licensing code.`
    );
  };

  // On load/reload, resolve the number again even when the submitted JSON
  // already contains an ID. External submissions may omit the ID, and an old
  // saved ID may point at a superseded version. Lookup endpoints select the
  // latest valid source; product data then comes only from incoming-tc-info.
  useEffect(() => {
    if (isReturn) return;
    const incomingTCNo = componentData.tcIncomingTCNo;
    const incomingATCNo = componentData.tcIncomingATCNo;
    const incomingRMDFNo = componentData.tcIncomingRMDFNo;
    const incomingFPFNo = componentData.tcIncomingFPFNo;
    const licenseCodeRaw = componentData.incomingTcLicensingCode || componentData.licensingCode || "";

    const licenseValueForForm = licenseCodeRaw
      ? licenseCodeRaw.startsWith("CB-")
        ? licenseCodeRaw
        : `CB-${licenseCodeRaw}`
      : "";

    if (licenseValueForForm) {
      setSelectedLicensingCode(licenseValueForForm);
      try {
        formRef?.current?.setFieldValue("licensingCode", licenseValueForForm);
      } catch (e) {
        // ignore if formRef not yet available
      }
    }

    const atcSourceType: "ATU" | "GOTS" = hasCertTypeToken(incomingATCNo, "GOTS") ? "GOTS" : "ATU";
    const sourceTypeForLookup: "TC" | "RMDF" | "FPF" | "ATU" | "GOTS" = incomingTCNo
      ? "TC"
      : incomingRMDFNo
      ? "RMDF"
      : incomingFPFNo
      ? "FPF"
      : atcSourceType;
    const currentNo = incomingTCNo || incomingRMDFNo || incomingFPFNo || incomingATCNo || "";
    const currentLookupKey = buildIncomingSourceLookupKey(
      sourceTypeForLookup,
      licenseValueForForm,
      currentNo
    );
    const alreadyHandled = Boolean(currentLookupKey) && lastSearchedRef.current === currentLookupKey;

    const restoreSelections = () => {
      if (Array.isArray(componentData.tcIncomingTCStandard) && componentData.tcIncomingTCStandard.length > 0) {
        const normalized = normalizeStandards(componentData.tcIncomingTCStandard);
        setIncomingStandards(normalized);
        formRef?.current?.setFieldValue("tcIncomingTCStandard", normalized);
      }
      if (Array.isArray(componentData.tcIncomingProductNo) && componentData.tcIncomingProductNo.length > 0) {
        formRef?.current?.setFieldValue("tcIncomingProductNo", componentData.tcIncomingProductNo);
      }
    };

    restoreSelections();

    if (currentNo && !licenseValueForForm) {
      clearResolvedIncomingSource(sourceTypeForLookup, currentNo);
      return;
    }

    if (incomingTCNo) {
      setSelectedCertType('TC');
      formRef?.current?.setFieldValue("certificateType", 'TC');
      setActiveSearchField("tc");
      setProductIncomingTcNo(incomingTCNo);

      if (!alreadyHandled && licenseValueForForm && (isReturn || isValidated)) {
        resetIncomingFields();
        setSourceLookupError("");
        lastSearchedRef.current = currentLookupKey;
        const seq = ++searchSeqRef.current;
        getTCNoforSearch(incomingTCNo, 0, "query", licenseValueForForm).then((res) => {
          if (seq !== searchSeqRef.current) return;
          const selected = res.find((r) => r.value === incomingTCNo);
          if (!selected?.tcId) {
            clearResolvedIncomingSource("TC", incomingTCNo);
            return;
          }
          setSourceLookupError("");
          const incomingType: IncomingSourceType = String(selected.sourceVersion || "").toLowerCase().includes("v3") ? "V3" : "V4";
          formRef?.current?.setFieldValue("tcIncomingTcId", selected.tcId);
          formRef?.current?.setFieldValue("tcIncomingLedgerTransactionId", "");
          setComponentData({
            tcIncomingTcId: selected.tcId,
            tcIncomingType: incomingType,
            tcIncomingLedgerTransactionId: "",
            tcProductIncomingTcV4Issued: incomingV4IssuedState,
          });
          fetchIncomingProductArticles(selected.tcId, incomingType);
        });
      } else if (!alreadyHandled && componentData.tcIncomingTcId && (isReturn || isValidated)) {
        setSourceLookupError("");
        const incomingType: IncomingSourceType = String(componentData.tcIncomingType || "V4").toUpperCase() === "V3" ? "V3" : "V4";
        fetchIncomingProductArticles(componentData.tcIncomingTcId, incomingType);
      }
    } else if (incomingRMDFNo) {
      setSelectedCertType('RMDF');
      formRef?.current?.setFieldValue("certificateType", 'RMDF');
      setActiveSearchField("rmdf");
      setProductIncomingTcNo(incomingRMDFNo);

      if (!alreadyHandled && licenseValueForForm && (isReturn || isValidated)) {
        resetIncomingFields();
        setSourceLookupError("");
        lastSearchedRef.current = currentLookupKey;
        const seq = ++searchSeqRef.current;
        getRMDFforSearch(incomingRMDFNo, 0, "query", licenseValueForForm).then((res) => {
          if (seq !== searchSeqRef.current) return;
          const selected = res.find((r) => r.value === incomingRMDFNo);
          const sourceId = selected?.mdfId || "";
          if (selected && sourceId) {
            setSourceLookupError("");
            formRef?.current?.setFieldValue("tcIncomingTcId", sourceId);
            formRef?.current?.setFieldValue("tcIncomingLedgerTransactionId", "");
            setComponentData({
              tcIncomingType: "RMDF",
              tcProductIncomingTcV4Issued: "N",
              tcIncomingTcId: sourceId,
              tcIncomingLedgerTransactionId: "",
            });
            fetchIncomingProductArticles(sourceId, "RMDF");
          } else {
            clearResolvedIncomingSource("RMDF", incomingRMDFNo);
          }
        });
      } else if (!alreadyHandled && componentData.tcIncomingTcId && (isReturn || isValidated)) {
        setSourceLookupError("");
        fetchIncomingProductArticles(componentData.tcIncomingTcId, "RMDF");
      }
    } else if (incomingFPFNo) {
      setSelectedCertType('FPF');
      formRef?.current?.setFieldValue("certificateType", 'FPF');
      setActiveSearchField("fpf");
      setProductIncomingTcNo(incomingFPFNo);

      if (!alreadyHandled && licenseValueForForm && (isReturn || isValidated)) {
        resetIncomingFields();
        setSourceLookupError("");
        lastSearchedRef.current = currentLookupKey;
        const seq = ++searchSeqRef.current;
        getFPFforSearch(incomingFPFNo, 0, "query", licenseValueForForm).then((res) => {
          if (seq !== searchSeqRef.current) return;
          const selected = res.find((r) => r.value === incomingFPFNo);
          const sourceId = selected?.fpfId || "";
          if (selected && sourceId) {
            setSourceLookupError("");
            formRef?.current?.setFieldValue("tcIncomingTcId", sourceId);
            formRef?.current?.setFieldValue("tcIncomingLedgerTransactionId", "");
            setComponentData({
              tcIncomingType: "FPF",
              tcProductIncomingTcV4Issued: "N",
              tcIncomingTcId: sourceId,
              tcIncomingLedgerTransactionId: "",
              tcIncomingFpfId: selected.fpfId || componentData.tcIncomingFpfId || "",
            });
            fetchIncomingProductArticles(sourceId, "FPF");
          } else {
            clearResolvedIncomingSource("FPF", incomingFPFNo);
          }
        });
      } else if (!alreadyHandled && componentData.tcIncomingTcId && (isReturn || isValidated)) {
        setSourceLookupError("");
        fetchIncomingProductArticles(componentData.tcIncomingTcId, "FPF");
      }
    } else if (incomingATCNo) {
      const certType = atcSourceType;

      setSelectedCertType(certType);
      formRef?.current?.setFieldValue("certificateType", certType);
      setActiveSearchField("atc");
      setProductIncomingTcNo(incomingATCNo);

      if (!alreadyHandled && licenseValueForForm && (isReturn || isValidated)) {
        resetIncomingFields();
        setSourceLookupError("");
        lastSearchedRef.current = currentLookupKey;
        const seq = ++searchSeqRef.current;
        const sourceType: IncomingSourceType = certType === "GOTS" ? "GOTS" : "ATU";
        getInputATCNoForSearch(
          incomingATCNo,
          0,
          "query",
          licenseValueForForm,
          certType
        ).then((res) => {
          if (seq !== searchSeqRef.current) return;
          const selected = res.find((r) => r.value === incomingATCNo);
          if (!selected?.atcId) {
            clearResolvedIncomingSource(certType, incomingATCNo);
            return;
          }
          setSourceLookupError("");
          formRef?.current?.setFieldValue("tcIncomingTcId", selected.atcId);
          formRef?.current?.setFieldValue("tcIncomingAtcId", selected.atcId);
          formRef?.current?.setFieldValue("tcIncomingLedgerTransactionId", "");
          setComponentData({
            tcIncomingTcId: selected.atcId,
            tcIncomingAtcId: selected.atcId,
            tcIncomingType: sourceType,
            tcIncomingLedgerTransactionId: "",
            tcProductIncomingTcV4Issued: "N",
          });
          fetchIncomingATCDetails(selected.atcId, sourceType);
        });
      } else if (!alreadyHandled && (isReturn || isValidated)) {
        const sourceType: IncomingSourceType = certType === "GOTS" ? "GOTS" : "ATU";
        const existingId = componentData.tcIncomingTcId || componentData.tcIncomingAtcId;
        if (existingId) {
          setSourceLookupError("");
          fetchIncomingATCDetails(existingId, sourceType);
        }
      }
    } else {
      setActiveSearchField(null);
      resetIncomingFields();
    }

  }, [componentData.tcIncomingTCNo, componentData.tcIncomingATCNo, componentData.tcIncomingRMDFNo, componentData.tcIncomingFPFNo, componentData.incomingTcLicensingCode, componentData.licensingCode]);

  const StyledScrollBox = styled(Box)(({ theme }) => ({
    borderRadius: "12px",
    border: "1px solid #ccc",
    backgroundColor: "#FFFFFF",
    padding: theme.spacing(1),
    height: "110px",
    overflowY: "auto",
    color: "#1C1C1C",
    fontSize: "0.875rem", 
    lineHeight: 1.4,
    paddingRight: theme.spacing(0.5),
  }));

  useEffect(() => {
    if (TE_ID) {
      fetchProductOptions();
    }
  }, [TE_ID]);

  const getCBLicensingCodes = async () => {
    if (cachedLicensingCodes && cachedLicensingCodes.length > 0) {
      setLicensingCodes(cachedLicensingCodes);
      return;
    }
    try {
      const result: any = await dtrackItService.getCBLicensingCodes();
      const cbLicensing = result.data.map((t: any) => {
        const code = t.code_1.replace(/^CB-/, '');
        return {
          value: t.code_1, 
          label: code, 
        };
      });
      cachedLicensingCodes = cbLicensing;
      setLicensingCodes(cbLicensing);
    } catch (error) {
      console.error('Error fetching licensing codes:', error);
    }
  }; 

  const getCertificateTypeOptions = async () => {
    if (cachedCertificateTypes && cachedCertificateTypes.length > 0) {
      setCertificateTypeOptions(cachedCertificateTypes);
      return;
    }
    try {
      const result: any = await dtrackItService.getIncomingCertificateTypes();
      const referenceOptions = (result?.data || []).map((item: any) => ({
        value: item.code_1,
        label: item.description_1,
      }));
      const options = ensureExternalIncomingCertificateTypes(referenceOptions);
      cachedCertificateTypes = options;
      setCertificateTypeOptions(options);
    } catch (error) {
      console.error("Error fetching certificate type options:", error);
    }
  };

  useEffect(() => {
    getCBLicensingCodes();
    getCertificateTypeOptions();
  }, []);

  useEffect(() => {
    if (isReturn) return;
    const issuedFlag = normalizeIncomingV4IssuedFlag(
      componentData.tcProductIncomingTcV4Issued
    );
    setIncomingV4IssuedState(issuedFlag);
    formRef?.current?.setFieldValue("tcProductIncomingTcV4Issued", issuedFlag);
  }, [componentData.tcProductIncomingTcV4Issued, formRef, isReturn]);

const validationSchema = Yup.object().shape({
  tcAssociatedProductArticleNo: Yup.string().required("Please select Associated Product Article Number"),
  tcProductIncomingTcV4Issued: Yup.string()
    .oneOf(["Y", "N"])
    .required("Please select Incoming TC v4 Issued"),
  certificateType: Yup.string().required("Please select Choose Type of Certificate"),
  licensingCode: Yup.string().required("Please select Choose CB Licensing Code"),
  searchCertificateNumber: Yup.string().required("Incoming Certificate Number is required"),
  tcIncomingTCNo: Yup.string().when('certificateType', {
    is: 'TC',
    then: (schema) =>
      schema
        .required('Incoming Certificate Number is required')
        .test('valid-incoming-tc-no', 'Incoming TC No cannot be blank or a placeholder value', hasValidIncomingText),
    otherwise: (schema) => schema.notRequired(),
  }),
  tcIncomingATCNo: Yup.string().when('certificateType', {
    is: (certificateType: string) => ['GOTS', 'ATU'].includes(certificateType),
    then: (schema) =>
      schema
        .required('Incoming Certificate Number is required')
        .test('valid-incoming-atc-no', 'Incoming ATC No cannot be blank or a placeholder value', hasValidIncomingText),
    otherwise: (schema) => schema.notRequired(),
  }),
  tcIncomingRMDFNo: Yup.string().when('certificateType', {
    is: 'RMDF',
    then: (schema) =>
      schema
        .required('Incoming Certificate Number is required')
        .test('valid-incoming-rmdf-no', 'Incoming RMDF No cannot be blank or a placeholder value', hasValidIncomingText),
    otherwise: (schema) => schema.notRequired(),
  }),
  tcIncomingFPFNo: Yup.string().when('certificateType', {
    is: 'FPF',
    then: (schema) =>
      schema
        .required('Incoming Certificate Number is required')
        .test('valid-incoming-fpf-no', 'Incoming FPF No cannot be blank or a placeholder value', hasValidIncomingText),
    otherwise: (schema) => schema.notRequired(),
  }),
  // Product article selection moved to the next step (checkbox table),
  // so it is no longer validated here.
  tcIncomingProductNo: Yup.array().notRequired(),
  tcIncomingTCStandard: Yup.array().notRequired(),
});

  const handleIncomingV4IssuedChange = (
    value: any,
    formValues?: any,
    setFieldValue?: any
  ) => {
    const issuedFlag = normalizeIncomingV4IssuedFlag(
      value?.target?.value ?? value
    );
    const certificateType = issuedFlag === "Y" ? "TC" : "";

    if (typeof setFieldValue === "function") {
      setFieldValue("tcProductIncomingTcV4Issued", issuedFlag);
      setFieldValue("certificateType", certificateType);
    } else {
      formRef?.current?.setFieldValue(
        "tcProductIncomingTcV4Issued",
        issuedFlag
      );
      formRef?.current?.setFieldValue("certificateType", certificateType);
    }

    searchSeqRef.current += 1;
    lastSearchedRef.current = "";
    setSourceLookupError("");
    setIncomingV4IssuedState(issuedFlag);
    setSelectedCertType(certificateType);
    setProductIncomingTcNo("");
    setActiveSearchField(null);
    resetIncomingFields();
    setIsValidated(isReturn);
    setAuthSellerTeId("");
    setAuthBuyerCountry("");
    setAuthTcStandards([]);
    setValidationError("");
    formRef?.current?.setFieldValue("searchCertificateNumber", "");
    formRef?.current?.setFieldValue("tcIncomingTCNo", "");
    formRef?.current?.setFieldValue("tcIncomingATCNo", "");
    formRef?.current?.setFieldValue("tcIncomingRMDFNo", "");
    formRef?.current?.setFieldValue("tcIncomingFPFNo", "");

    setComponentData({
      certificateType,
      tcIncomingTCNo: "",
      tcIncomingATCNo: "",
      tcIncomingRMDFNo: "",
      tcIncomingFPFNo: "",
      tcIncomingProductNo: [],
      tcIncomingTCStandard: [],
      tcIncomingTcId: "",
      tcIncomingAtcId: "",
      tcIncomingLedgerTransactionId: "",
      tcIncomingFpfId: "",
      tcIncomingType: "",
      tcProductIncomingTcV4Issued: issuedFlag,
      incomingProductStandardsByArticle: {},
      incomingProductSourceByArticle: {},
      incomingProductRawMaterialsByArticle: {},
      incomingProductMaxWeightByArticle: {},
    });
  };

  const handleCertificateTypeChange = (event: any) => {
    const value = event?.target?.value || "";
    // Invalidate any in-flight search results for the previous type/number.
    searchSeqRef.current += 1;
    lastSearchedRef.current = "";
    setSourceLookupError("");
    setSelectedCertType(value); 
    formRef?.current?.setFieldValue("certificateType", value);
  
    // Issue 3: Reset all certificate-related fields when type changes
    formRef?.current?.setFieldValue("tcIncomingTCNo", "");
    formRef?.current?.setFieldValue("tcIncomingATCNo", "");
    formRef?.current?.setFieldValue("tcIncomingRMDFNo", "");
    formRef?.current?.setFieldValue("tcIncomingFPFNo", "");
    formRef?.current?.setFieldValue("searchCertificateNumber", "");
    formRef?.current?.setFieldValue("tcIncomingTcId", "");
    formRef?.current?.setFieldValue("tcIncomingAtcId", "");
    formRef?.current?.setFieldValue("tcIncomingLedgerTransactionId", "");
    
    setProductIncomingTcNo("");
    setActiveSearchField(null);
    resetIncomingFields();
    setIsValidated(value === "RMDF" || value === "FPF" || isReturn);
    
    // Propagate the changes to parent.
    // certificateType MUST be persisted here: FormBuilder re-initializes from
    // componentData on every parent update, so a value living only in Formik
    // state gets wiped and the user has to select the type twice.
    setComponentData({
      certificateType: value,
      tcIncomingTCNo: "",
      tcIncomingATCNo: "",
      tcIncomingRMDFNo: "",
      tcIncomingFPFNo: "",
      tcIncomingProductNo: [],
      tcIncomingTCStandard: [],
      tcIncomingTcId: "",
      tcIncomingAtcId: "",
      tcIncomingLedgerTransactionId: "",
      tcIncomingType: "",
      tcProductIncomingTcV4Issued: incomingV4IssuedState,
      incomingProductStandardsByArticle: {},
      incomingProductSourceByArticle: {},
      incomingProductRawMaterialsByArticle: {},
      incomingProductMaxWeightByArticle: {},
    });
  };

  const handleLicensingValueChange = (event: any) => {
  const value = event?.target?.value || "";
  setSourceLookupError("");
  setSelectedLicensingCode(value);
  formRef?.current?.setFieldValue("licensingCode", value);
  // Persist into componentData so form re-initialization (and edit mode)
  // keeps the CB licensing code instead of falling back to the placeholder.
  setComponentData({
    incomingTcLicensingCode: String(value).replace(/^CB-/, ""),
  });
};

  // Accepts FormBuilder's onChange signature (value, formValues, setFieldValue)
  const handleAssociatedProductChange = (value: any, formValues?: any, setFieldValue?: any) => {
    const newVal = value?.target?.value ?? value?.value ?? value ?? "";
    try {
      if (typeof setFieldValue === 'function') {
        setFieldValue('tcAssociatedProductArticleNo', newVal);
      } else {
        formRef?.current?.setFieldValue('tcAssociatedProductArticleNo', newVal);
      }
    } catch (e) {
      // ignore if formRef not ready
    }
    // Propagate immediately to parent so parent Save sees the latest selection
    setComponentData({ ...(componentData || {}), tcAssociatedProductArticleNo: newVal });
  };

  useEffect(() => {
    const formValue = formRef?.current?.values?.licensingCode;
    if (formValue && formValue !== selectedLicensingCode) {
      setSelectedLicensingCode(formValue);
    }
  }, [formRef?.current?.values?.licensingCode]);
  
  

  const formConfig: FormConfigProps = {
    initialValues: {
      ...componentData,
      // Retained only for backend compatibility. DAT 501 removes the farm flag
      // from the UI and requires every incoming source to follow this workflow.
      isFlagSeller: "No",
      tcProductIncomingTcV4Issued: isReturn
        ? (isV4 ? "Y" : "N")
        : normalizeIncomingV4IssuedFlag(componentData.tcProductIncomingTcV4Issued),
      certificateType: isReturn
        ? "TC"
        : normalizeIncomingV4IssuedFlag(
            componentData.tcProductIncomingTcV4Issued
          ) === "Y"
        ? "TC"
        : componentData.certificateType || (
            // The number fields are already type-specific, and RMDF/FPF/ATU numbers
            // may carry the type token anywhere (RMDF_CUC_1 or CUC_RMDF_1), so key
            // off field presence and only disambiguate GOTS vs ATU by token.
            componentData.tcIncomingTCNo ? 'TC' :
            componentData.tcIncomingRMDFNo ? 'RMDF' :
            componentData.tcIncomingFPFNo ? 'FPF' :
            hasCertTypeToken(componentData.tcIncomingATCNo, 'GOTS') ? 'GOTS' :
            componentData.tcIncomingATCNo ? 'ATU' : ""
          ),
      licensingCode: isReturn
        ? licenseValue
        : (componentData.incomingTcLicensingCode ? (componentData.incomingTcLicensingCode.startsWith("CB-") ? componentData.incomingTcLicensingCode : `CB-${componentData.incomingTcLicensingCode}`) : ""), 
      tcIncomingTCStandard: normalizeStandards(componentData.tcIncomingTCStandard),
      tcIncomingProductNo: Array.isArray(componentData.tcIncomingProductNo)
        ? componentData.tcIncomingProductNo
        : componentData.tcIncomingProductNo
        ? [componentData.tcIncomingProductNo]
        : [],
      tcIncomingRMDFNo: componentData.tcIncomingRMDFNo || "",
      tcIncomingFPFNo: componentData.tcIncomingFPFNo || "",
      tcIncomingLedgerTransactionId: componentData.tcIncomingLedgerTransactionId || "",
      searchCertificateNumber: isReturn ? returnTcNo : (componentData.tcIncomingTCNo || componentData.tcIncomingATCNo || componentData.tcIncomingRMDFNo || componentData.tcIncomingFPFNo || ""),
      tcIncomingTCNo: isReturn ? returnTcNo : (componentData.tcIncomingTCNo || ""),
      tcIncomingTcId: isReturn ? returnTcId : (componentData.tcIncomingTcId || ""),
    },
    validationSchema,
    sections: [
      {
        fields: [
          {
            label: "Associated Product Article Number",
            name: "tcAssociatedProductArticleNo",
            type: "select",
            gridSize: { xs: 12, md: 6 },
            required: true,
            readOnly: hasSubmitTc,
            events: {
              onChange: handleAssociatedProductChange,
            },
            props: {
              options: productOptions,
              enableSearch: true,
              placeholder: "Select Product Article No",
              fullWidth: true,
            },
          },
          {
            label: "Is the incoming information a TC V4?",
            name: "tcProductIncomingTcV4Issued",
            type: "radio",
            gridSize: { xs: 12, md: 6 },
            required: true,
            readOnly: hasSubmitTc || isReturn,
            events: {
              onChange: handleIncomingV4IssuedChange,
            },
            props: {
              options: [
                { value: "Y", label: "Yes" },
                { value: "N", label: "No" },
              ],
              disabled: hasSubmitTc || isReturn,
            },
          },
          {
            type: "component",
            label: "",
            name: "infoMessage",
            required: false,
            gridSize: { xs: 12, md: 12 },
            children: (
              <Box sx={{ color: 'text.secondary', mt: 1.5, mb: 1.5, fontSize: '0.875rem' }}>
                Choose the option and search the certificate number below for the Product Incoming TC number.
              </Box>
            ),
          },
        ],
      },
      {
        fields: [
          {
            label: "Choose Type of Certificate",
            name: "certificateType",
            type: "select",
            required: true,
            gridSize: { xs: 12, md: 4},
            props: {
              options: certificateTypes,
              onChange: handleCertificateTypeChange,
              select: true,
              native: false,
              fullWidth: true,
              placeholder: 'Select Certificate Type',
              disabled: isReturn,
            },
          },
          {
            label: "Choose CB Licensing Code",
            name: "licensingCode",
            type: "select",
            required: true,
            gridSize: { xs: 12, md: 4},
            props: {
              options: (selectedCertType === "FPF" || selectedCertType === "RMDF") && loggedInCbLicensingCode
                ? licensingCodes.filter((opt) => opt.value === (loggedInCbLicensingCode.startsWith("CB-") ? loggedInCbLicensingCode : `CB-${loggedInCbLicensingCode}`))
                : licensingCodes,
              select: true,
              native: false,
              fullWidth: true,
              enableSearch: true,
              onChange: handleLicensingValueChange,
              placeholder: 'Select Licensing Code',
              disabled: isReturn || selectedCertType === "FPF" || selectedCertType === "RMDF",
            },
          },
          {
            label: "Search for the Certificate Number",
            name: "searchCertificateNumber",
            type: "search",
            required: selectedCertType ? true : false,
            gridSize: { xs: 12, md: 4 },
            props: {
              onChange: handleCertificateNumberChange,
              onSearch: (query: string, offset: number) => {
                if (isReturn) {
                  return Promise.resolve([]);
                }
                if (!selectedCertType || !selectedLicensingCode) {
                  return Promise.resolve([]);
                }
                
                if (query === 'na' || !query || query.trim() === '') {
                  return Promise.resolve([]);
                }
                
                if (selectedCertType === 'TC') {
                  return getTCNoforSearch(query, offset, "query", selectedLicensingCode);
                } else if (selectedCertType === 'RMDF') {
                  return getRMDFforSearch(query, offset, "query", selectedLicensingCode);
                } else if (selectedCertType === 'FPF') {
                  return getFPFforSearch(query, offset, "query", selectedLicensingCode);
                } else if (selectedCertType === 'GOTS' || selectedCertType === 'ATU') {
                  return getInputATCNoForSearch(query, offset, "query", selectedLicensingCode);
                }
                return Promise.resolve([]);
              },
              displayKey: "label",
              fullWidth: true,
              disabled: !selectedCertType || !selectedLicensingCode || isReturn,
              placeholder: selectedCertType === 'TC' ? 'Search TC Number' : selectedCertType === 'RMDF' ? 'Search RMDF Number' : selectedCertType === 'FPF' ? 'Search FPF Number' : 'Search ATU Number',
              value: productIncomingTcNo,
            },
          },
        ],
      },
      {
        fields: [
          {
            type: "component",
            label: "",
            name: "selectedDocumentCard",
            required: false,
            gridSize: { xs: 12, md: 12 },
            children: (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2.5,
                  p: 2.5,
                  mt: 2.5,
                  borderRadius: "12px",
                  border: "1.5px solid #F0CD7F",
                  backgroundColor: "#FCF8F2",
                  boxShadow: "0 2px 8px rgba(240, 205, 127, 0.15)",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        fontSize: "0.75rem",
                        letterSpacing: "0.5px",
                      }}
                    >
                      Selected Document
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 600,
                        color: "#1C1C1C",
                        mt: 0.5,
                        fontFamily: "monospace",
                        fontSize: "1rem",
                      }}
                    >
                      {productIncomingTcNo || "No certificate selected yet"}
                    </Typography>
                  </Box>
                  {isValidated || selectedCertType === "RMDF" || selectedCertType === "FPF" ? (
                    <Chip label="Verified & Validated" color="success" size="small" sx={{ fontWeight: 600 }} />
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                        fontStyle: "italic",
                        fontSize: "0.875rem",
                      }}
                    >
                      Security verification required before proceeding.
                    </Typography>
                  )}
                </Box>

                {productIncomingTcNo && !isReturn && !isValidated && selectedCertType !== "RMDF" && selectedCertType !== "FPF" && (
                  <Box
                    sx={{
                      mt: 2,
                      pt: 2.5,
                      borderTop: "1px solid #e2e8f0",
                      display: "flex",
                      flexDirection: "column",
                      gap: 2.5,
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 700,
                        color: "#000000",
                        fontSize: "0.95rem",
                        letterSpacing: "0.01em",
                      }}
                    >
                      For Security Reasons, please answer the following questions and validate to proceed
                    </Typography>

                    {validationError && renderFriendlyValidationError(validationError)}

                    {/* ── Type-Specific Security Questionnaire Fields ── */}
                    {selectedCertType === "RMDF" ? (
                      <>
                        {/* RMDF Field 1: Seller TE-ID */}
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                          <Typography variant="body2" sx={{ color: "#000000", fontSize: "0.875rem", lineHeight: 1.4 }}>
                            <Box component="span" sx={{ fontWeight: 700 }}>
                              Seller TE-ID
                            </Box>{" "}
                            <Box component="span" sx={{ fontStyle: "italic", fontWeight: 700 }}>
                              (Please select the Seller TE-ID from RMDF: {productIncomingTcNo})
                            </Box>
                          </Typography>
                          <Autocomplete
                            options={sellerTeIdOptions}
                            loading={isFetchingTeIds}
                            forcePopupIcon={true}
                            filterOptions={(options, state) => {
                              const q = state.inputValue.trim().toLowerCase();
                              if (!q) return options;
                              return options.filter((opt) =>
                                opt.label.toLowerCase().includes(q) || opt.value.toLowerCase().includes(q)
                              );
                            }}
                            freeSolo
                            getOptionLabel={(option) => (typeof option === "string" ? option : option.label || option.value || "")}
                            value={sellerTeIdOptions.find((opt) => opt.value === authSellerTeId) || (authSellerTeId ? { value: authSellerTeId, label: authSellerTeId } : null)}
                            onInputChange={(_e, newInputValue, reason) => {
                              if (reason === "input") {
                                setAuthSellerTeId(newInputValue);
                                fetchSellerTeIdOptions(newInputValue);
                              }
                            }}
                            onChange={(_e, val) => {
                              if (typeof val === "string") setAuthSellerTeId(val);
                              else if (val && val.value) setAuthSellerTeId(val.value);
                              else setAuthSellerTeId("");
                            }}
                            renderInput={(params) => (
                              <TextField {...params} size="small" placeholder="Select or search Seller TE-ID" sx={{ "& .MuiOutlinedInput-root": { borderRadius: "4px", backgroundColor: "#fff" } }} />
                            )}
                          />
                        </Box>

                        {/* RMDF Field 2: Buyer Certified Organization TE-ID */}
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                          <Typography variant="body2" sx={{ color: "#000000", fontSize: "0.875rem", lineHeight: 1.4 }}>
                            <Box component="span" sx={{ fontWeight: 700 }}>
                              Buyer Certified Organization TE-ID
                            </Box>{" "}
                            <Box component="span" sx={{ fontStyle: "italic", fontWeight: 700 }}>
                              (Please select or enter the Buyer Certified Organization TE-ID from RMDF: {productIncomingTcNo})
                            </Box>
                          </Typography>
                          <Autocomplete
                            options={sellerTeIdOptions}
                            loading={isFetchingTeIds}
                            forcePopupIcon={true}
                            filterOptions={(options, state) => {
                              const q = state.inputValue.trim().toLowerCase();
                              if (!q) return options;
                              return options.filter((opt) =>
                                opt.label.toLowerCase().includes(q) || opt.value.toLowerCase().includes(q)
                              );
                            }}
                            freeSolo
                            getOptionLabel={(option) => (typeof option === "string" ? option : option.label || option.value || "")}
                            value={sellerTeIdOptions.find((opt) => opt.value === authBuyerTeId) || (authBuyerTeId ? { value: authBuyerTeId, label: authBuyerTeId } : null)}
                            onInputChange={(_e, newInputValue, reason) => {
                              if (reason === "input") {
                                setAuthBuyerTeId(newInputValue);
                                fetchSellerTeIdOptions(newInputValue);
                              }
                            }}
                            onChange={(_e, val) => {
                              if (typeof val === "string") setAuthBuyerTeId(val);
                              else if (val && val.value) setAuthBuyerTeId(val.value);
                              else setAuthBuyerTeId("");
                            }}
                            renderInput={(params) => (
                              <TextField {...params} size="small" placeholder="Select or search Buyer TE-ID" sx={{ "& .MuiOutlinedInput-root": { borderRadius: "4px", backgroundColor: "#fff" } }} />
                            )}
                          />
                        </Box>
                      </>
                    ) : selectedCertType === "FPF" ? (
                      <>
                        {/* FPF Field 1: Certified Organization TE-ID */}
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                          <Typography variant="body2" sx={{ color: "#000000", fontSize: "0.875rem", lineHeight: 1.4 }}>
                            <Box component="span" sx={{ fontWeight: 700 }}>
                              Certified Organization TE-ID
                            </Box>{" "}
                            <Box component="span" sx={{ fontStyle: "italic", fontWeight: 700 }}>
                              (Please select the Certified Organization TE-ID from FPF: {productIncomingTcNo})
                            </Box>
                          </Typography>
                          <Autocomplete
                            options={sellerTeIdOptions}
                            loading={isFetchingTeIds}
                            forcePopupIcon={true}
                            filterOptions={(options, state) => {
                              const q = state.inputValue.trim().toLowerCase();
                              if (!q) return options;
                              return options.filter((opt) =>
                                opt.label.toLowerCase().includes(q) || opt.value.toLowerCase().includes(q)
                              );
                            }}
                            freeSolo
                            getOptionLabel={(option) => (typeof option === "string" ? option : option.label || option.value || "")}
                            value={sellerTeIdOptions.find((opt) => opt.value === authCertifiedOrgTeId) || (authCertifiedOrgTeId ? { value: authCertifiedOrgTeId, label: authCertifiedOrgTeId } : null)}
                            onInputChange={(_e, newInputValue, reason) => {
                              if (reason === "input") {
                                setAuthCertifiedOrgTeId(newInputValue);
                                fetchSellerTeIdOptions(newInputValue);
                              }
                            }}
                            onChange={(_e, val) => {
                              if (typeof val === "string") setAuthCertifiedOrgTeId(val);
                              else if (val && val.value) setAuthCertifiedOrgTeId(val.value);
                              else setAuthCertifiedOrgTeId("");
                            }}
                            renderInput={(params) => (
                              <TextField {...params} size="small" placeholder="Select or search Certified Org TE-ID" sx={{ "& .MuiOutlinedInput-root": { borderRadius: "4px", backgroundColor: "#fff" } }} />
                            )}
                          />
                        </Box>

                        {/* FPF Field 2: Scope Certificate Number (SC No) */}
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                          <Typography variant="body2" sx={{ color: "#000000", fontSize: "0.875rem", lineHeight: 1.4 }}>
                            <Box component="span" sx={{ fontWeight: 700 }}>
                              Certified Organization Scope Certificate Number (SC No)
                            </Box>{" "}
                            <Box component="span" sx={{ fontStyle: "italic", fontWeight: 700 }}>
                              (Please enter the SC No e.g. XXX-SC-XXXXXXXX-XXXX-XX from FPF: {productIncomingTcNo})
                            </Box>
                          </Typography>
                          <TextField
                            size="small"
                            placeholder="Enter Scope Certificate Number (e.g. XXX-SC-XXXX)"
                            value={authScNo}
                            onChange={(e) => setAuthScNo(e.target.value)}
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "4px", backgroundColor: "#fff" } }}
                          />
                        </Box>
                      </>
                    ) : (
                      <>
                        {/* TC / ATU / GOTS Field 1: Seller TE-ID */}
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                          <Typography variant="body2" sx={{ color: "#000000", fontSize: "0.875rem", lineHeight: 1.4 }}>
                            <Box component="span" sx={{ fontWeight: 700 }}>
                              Seller TE-ID
                            </Box>{" "}
                            <Box component="span" sx={{ fontStyle: "italic", fontWeight: 700 }}>
                              (Please select the TE-ID of the Seller from the {selectedCertType || "TC"}: {productIncomingTcNo})
                            </Box>
                          </Typography>
                          <Autocomplete
                            options={sellerTeIdOptions}
                            loading={isFetchingTeIds}
                            forcePopupIcon={true}
                            filterOptions={(options, state) => {
                              const q = state.inputValue.trim().toLowerCase();
                              if (!q) return options;
                              return options.filter((opt) =>
                                opt.label.toLowerCase().includes(q) || opt.value.toLowerCase().includes(q)
                              );
                            }}
                            freeSolo
                            getOptionLabel={(option) => {
                              if (typeof option === "string") return option;
                              return option.label || option.value || "";
                            }}
                            value={
                              sellerTeIdOptions.find((opt) => opt.value === authSellerTeId) ||
                              (authSellerTeId ? { value: authSellerTeId, label: authSellerTeId } : null)
                            }
                            onInputChange={(_e, newInputValue, reason) => {
                              if (reason === "input") {
                                setAuthSellerTeId(newInputValue);
                                fetchSellerTeIdOptions(newInputValue);
                              }
                            }}
                            onChange={(_e, val) => {
                              if (typeof val === "string") {
                                setAuthSellerTeId(val);
                              } else if (val && val.value) {
                                setAuthSellerTeId(val.value);
                              } else {
                                setAuthSellerTeId("");
                              }
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                size="small"
                                placeholder="Select or search Seller TE-ID"
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: "4px",
                                    backgroundColor: "#fff",
                                    "&:hover fieldset": { borderColor: "#999" },
                                    "&.Mui-focused fieldset": { borderColor: "#e5c158" },
                                  },
                                }}
                              />
                            )}
                          />
                        </Box>

                        {/* TC / ATU / GOTS Field 2: Buyer Country */}
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                          <Typography variant="body2" sx={{ color: "#000000", fontSize: "0.875rem", lineHeight: 1.4 }}>
                            <Box component="span" sx={{ fontWeight: 700 }}>
                              Buyer Country
                            </Box>{" "}
                            <Box component="span" sx={{ fontStyle: "italic", fontWeight: 700 }}>
                              (Please select the Buyer Country from the {selectedCertType || "TC"}: {productIncomingTcNo})
                            </Box>
                          </Typography>
                          <Autocomplete
                            options={countryOptionsList}
                            getOptionLabel={(option) => option.label}
                            value={countryOptionsList.find((c) => c.value === authBuyerCountry) || null}
                            onChange={(_e, val) => setAuthBuyerCountry(val?.value || "")}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                size="small"
                                placeholder="Select Buyer Country"
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: "4px",
                                    backgroundColor: "#fff",
                                  },
                                }}
                              />
                            )}
                          />
                        </Box>

                        {/* TC / ATU / GOTS Field 3: Applicable Standards */}
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                          <Typography variant="body2" sx={{ color: "#000000", fontSize: "0.875rem", lineHeight: 1.4 }}>
                            <Box component="span" sx={{ fontWeight: 700 }}>
                              {selectedCertType === "GOTS" ? "GOTS Standard" : "TC Standard"}
                            </Box>{" "}
                            <Box component="span" sx={{ fontStyle: "italic", fontWeight: 700 }}>
                              (Please select all the applicable standards from the {selectedCertType || "TC"}: {productIncomingTcNo})
                            </Box>
                          </Typography>
                          <Autocomplete
                            multiple
                            options={TC_STANDARDS}
                            getOptionLabel={(option) => option.label}
                            value={TC_STANDARDS.filter((s) => authTcStandards.includes(s.value))}
                            onChange={(_e, val) => setAuthTcStandards(val.map((item) => item.value))}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                size="small"
                                placeholder="Select Applicable Standards"
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: "4px",
                                    backgroundColor: "#fff",
                                  },
                                }}
                              />
                            )}
                          />
                        </Box>
                      </>
                    )}

                    {/* Action Button */}
                    <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1.5 }}>
                      <StyledButton
                        type="button"
                        onClick={handleValidateAndProceedIncomingTc}
                        disabled={
                          isValidatingTc ||
                          (selectedCertType === "RMDF" || selectedCertType === "FPF"
                            ? false
                            : !authSellerTeId || !authBuyerCountry || authTcStandards.length === 0)
                        }
                        sx={{
                          backgroundColor: "#e5c158",
                          color: "#000000",
                          fontWeight: 700,
                          fontSize: "0.875rem",
                          px: 3.5,
                          py: 0.9,
                          borderRadius: "20px",
                          border: "1px solid #111111",
                          boxShadow: "0 2px 4px rgba(0, 0, 0, 0.08)",
                          textTransform: "none",
                          transition: "all 0.2s ease-in-out",
                          "&:hover": {
                            backgroundColor: "#d8b245",
                            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.12)",
                          },
                          "&.Mui-disabled": {
                            backgroundColor: "#e2e5e9",
                            color: "#999999",
                            border: "1px solid #cccccc",
                          },
                        }}
                      >
                        {isValidatingTc ? "Validating..." : "Validate and Proceed"}
                      </StyledButton>
                    </Box>
                  </Box>
                )}
              </Box>
            ),
          },
        ],
      },
    ],
    handleSubmit: (values: any) => {
      const licensingRaw =
        values.licensingCode ||
        componentData.incomingTcLicensingCode ||
        componentData.licensingCode ||
        "";
      const licensingCodeOnly = String(licensingRaw).replace(/^CB-/, "");

      const payload = {
        tcAssociatedProductArticleNo: values.tcAssociatedProductArticleNo,
        certificateType: values.certificateType || componentData.certificateType || "",
        tcIncomingTCNo: values.tcIncomingTCNo || componentData.tcIncomingTCNo || "",
        tcIncomingATCNo: values.tcIncomingATCNo || componentData.tcIncomingATCNo || "",
        tcIncomingRMDFNo: values.tcIncomingRMDFNo || componentData.tcIncomingRMDFNo || "",
        tcIncomingFPFNo: values.tcIncomingFPFNo || componentData.tcIncomingFPFNo || "",
        tcIncomingProductNo:
          Array.isArray(values.tcIncomingProductNo) && values.tcIncomingProductNo.length > 0
            ? values.tcIncomingProductNo
            : componentData.tcIncomingProductNo || [],
        tcIncomingTCStandard:
          Array.isArray(values.tcIncomingTCStandard) && values.tcIncomingTCStandard.length > 0
            ? values.tcIncomingTCStandard
            : componentData.tcIncomingTCStandard || [],
        tcIncomingTcId: values.tcIncomingTcId || componentData.tcIncomingTcId || "",
        tcIncomingAtcId: values.tcIncomingAtcId || componentData.tcIncomingAtcId || "",
        tcIncomingLedgerTransactionId:
          values.tcIncomingLedgerTransactionId ||
          componentData.tcIncomingLedgerTransactionId ||
          "",
        tcIncomingFpfId: values.tcIncomingFpfId || componentData.tcIncomingFpfId || "",
        tcIncomingType: componentData.tcIncomingType || "",
        incomingTcLicensingCode: licensingCodeOnly,
        tcProductIncomingTcV4Issued: normalizeIncomingV4IssuedFlag(
          values.tcProductIncomingTcV4Issued ??
            componentData.tcProductIncomingTcV4Issued
        ),
        incomingProductStandardsByArticle: componentData.incomingProductStandardsByArticle || {},
        incomingProductSourceByArticle: componentData.incomingProductSourceByArticle || {},
        incomingProductRawMaterialsByArticle:
          componentData.incomingProductRawMaterialsByArticle || {},
        incomingProductMaxWeightByArticle: componentData.incomingProductMaxWeightByArticle || {},
        isFlagSeller: "No",
      };
      
      // Set component data without UI-only fields
      setComponentData(payload);
    },
  };

  return (
    <>
      {sourceLookupError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {sourceLookupError}
        </Alert>
      )}
      <FormBuilder formConfig={formConfig} onSubmit={formConfig.handleSubmit} formRef={formRef} />
    </>
  );
};

export default IncomingTcInfo;
